/* eslint-disable no-case-declarations */
import { flatten, isEqual } from "lodash-es";

import type WebApiClient from "../../api/WebApiClient.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import type { ITableSpecWithContext } from "../../ui-modules/content-browser/ContentBrowserTableView.js";
import { INITIAL_PROFILE_TRACK_DATA } from "../../ui-modules/genome-viewer/ProfileTrackLoader.js";
import { noTrackDataAvailable } from "../../ui-modules/genome-viewer/TrackSelectionDialog.js";
import { type IBedTrackData, INITIAL_BED_TRACK_DATA } from "../../ui-modules/genome-viewer/bed-track/BedTrackLoader.js";
import { DEFAULT_BED_TRACK_CONFIGURATION } from "../../ui-modules/genome-viewer/bed-track/BedsTrackCustomizationDialog.js";
import type { IConditionNameLookup } from "../../ui-modules/genome-viewer/features-track/FeatureTrack.js";
import {
  type IFeatureTrackData,
  INITIAL_FEATURE_TRACK_DATA,
  type ITrackCommunityAnnotationsData,
} from "../../ui-modules/genome-viewer/features-track/FeatureTrackLoader.js";
import {
  INITIAL_NGS_COVERAGE_TRACK_DATA,
  type INgsCoverageTrackData,
} from "../../ui-modules/genome-viewer/ngs-track/NgsCoverageTrackLoader.js";
import {
  INITIAL_NGS_READS_TRACK_DATA,
  type INgsReadsTooltip,
  type INgsReadsTrackData,
} from "../../ui-modules/genome-viewer/ngs-track/NgsReadsTrackLoader.js";
import {
  INITIAL_VARIANT_TRACK_DATA,
  type IVariantTrackData,
} from "../../ui-modules/genome-viewer/variants-track/VariantTrackLoader.js";
import { type IVariantFilterableProperty } from "../../ui-modules/genome-viewer/variants-track/configuration/VariantsTrackCustomizationDialog.js";
import type { IAppState } from "../reducer.js";
import { type ReduxAction, type ReduxDispatch, createReducer } from "../redux-utils.js";
import { updateUserParams } from "./auth.js";
import {
  type IFilterPayload,
  type ITableColumn,
  createTableSpecForAnalysisGroupParent,
  createTableSpecForCoordinateListParent,
  createTableSpecForFileParent,
  createTableSpecForOrganismParent,
  ensureRowIsVisible,
  sameTableSpec,
  selectTableRows,
  toggleSelection,
} from "./table-data.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import type { IColorSchemeForType, IColorSchemes } from "@/app-utils/genome-browser-color.ts";
import { mergeItemToArray } from "@/app-utils/immutable.ts";
import {
  ensureSafeContigRange,
  getExtendedRange,
  getPaddedRange,
  getRangeLength,
  rangeEquals,
  rangesIntersect,
} from "@/app-utils/range.ts";
import { type IRsqlComparisonExpr, SEARCH_OPERATORS, encodeFilter } from "@/app-utils/rsql.ts";
import type { TGridSelModelIdentifier } from "@/app-utils/selection-model.ts";
import type { Dictionary } from "@/app-utils/type.ts";
import { organismToMutantGenome } from "@/app-utils/variant.ts";
import type {
  AaFeatureStats,
  BamSummary,
  Contig,
  CoordinateList,
  CoverageRaster,
  Dataset,
  DatasetColumn,
  Feature,
  GenomeBrowserStatistics,
  GenomeSearch,
  GenomeViewState,
  GenomicFeature,
  Group,
  InputRegionWithoutStrand,
  InputRestriction,
  InputTableQuerySpec,
  InputUserParamsInput,
  MarkerStats,
  NuFeatureStats,
  Organism,
  OrganismPhenotype,
  Phenotype,
  PileupAlignment,
  PileupAlignmentSegment,
  PileupRow,
  ProjectFile,
  Range,
  Region,
  RegionFlag,
  SplitPaneLayoutState,
  TableQuerySpec,
  Track,
  TrackBedConfiguration,
  TrackBedOrderedBedFile,
  TrackCoordinateConfiguration,
  TrackCoordinateOrderedCoordinateList,
  TrackCustomization,
  TrackExpressionColoring,
  TrackFeatures,
  TrackGroupIndicator,
  TrackNgsConfiguration,
  TrackNgsOrderedBamFilesData,
  TrackNuProfile,
  TrackPhenotypeConfiguration,
  TrackVariants,
  Variant,
  VariantAnalysisGroup,
  VcfVariant,
  View,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import {
  ADD_BED_FILE,
  BAM_VALIDATION_FAILURE,
  BED_VALIDATION_FAILURE,
  CLEAR_GENOME_BROWSER_DATA,
  CONTIG_AND_RANGE_CHANGED,
  CONTIG_CHANGED,
  FEATURES_TABLE_STATE_CHANGED,
  GENOME_VIEWER_LAYOUT_CHANGED,
  HIGHLIGHT_GENOME_FEATURE,
  HIGHLIGHT_MUTANT_GENOME,
  LOAD_APPLICABLE_ORG_PROPERTY_TYPES_FAILURE,
  LOAD_APPLICABLE_ORG_PROPERTY_TYPES_REQUEST,
  LOAD_APPLICABLE_ORG_PROPERTY_TYPES_SUCCESS,
  LOAD_BAM_FILE_SUMMARY_FAILURE,
  LOAD_BAM_FILE_SUMMARY_SUCCESS,
  LOAD_BED_FILE_FAILURE,
  LOAD_BED_FILE_SUCCESS,
  LOAD_COORDINATE_LISTS_BY_GENOME_FAILURE,
  LOAD_COORDINATE_LISTS_BY_GENOME_REQUEST,
  LOAD_COORDINATE_LISTS_BY_GENOME_SUCCESS,
  LOAD_COORDINATE_LISTS_FAILURE,
  LOAD_COORDINATE_LIST_FAILURE,
  LOAD_COORDINATE_LIST_SUCCESS,
  LOAD_DATASET_FOR_TRACK_COLORING_FAILURE,
  LOAD_DATASET_FOR_TRACK_COLORING_REQUEST,
  LOAD_DATASET_FOR_TRACK_COLORING_SUCCESS,
  LOAD_GENOME_BROWSER_GENOME_FAILURE,
  LOAD_GENOME_BROWSER_GENOME_REQUEST,
  LOAD_GENOME_BROWSER_GENOME_SUCCESS,
  LOAD_GENOME_BROWSER_STATS_FAILURE,
  LOAD_GENOME_BROWSER_STATS_SUCCESS,
  LOAD_NGS_COVERAGE_FEATURES_FAILURE,
  LOAD_NGS_COVERAGE_FEATURES_REQUEST,
  LOAD_NGS_COVERAGE_FEATURES_SUCCESS,
  LOAD_NGS_PILEUP_FEATURES_FAILURE,
  LOAD_NGS_PILEUP_FEATURES_REQUEST,
  LOAD_NGS_PILEUP_FEATURES_SUCCESS,
  LOAD_NGS_READ_TOOLTIP_FAILURE,
  LOAD_NGS_READ_TOOLTIP_REQUEST,
  LOAD_NGS_READ_TOOLTIP_SUCCESS,
  LOAD_PROFILE_FEATURES_FAILURE,
  LOAD_PROFILE_FEATURES_REQUEST,
  LOAD_PROFILE_FEATURES_SUCCESS,
  LOAD_TRACK_BED_FEATURES_FAILURE,
  LOAD_TRACK_BED_FEATURES_REQUEST,
  LOAD_TRACK_BED_FEATURES_SUCCESS,
  LOAD_TRACK_COMMUNITY_ANNOTATIONS_FAILURE,
  LOAD_TRACK_COMMUNITY_ANNOTATIONS_REQUEST,
  LOAD_TRACK_COMMUNITY_ANNOTATIONS_SUCCESS,
  LOAD_TRACK_COORDINATE_FEATURES_FAILURE,
  LOAD_TRACK_COORDINATE_FEATURES_REQUEST,
  LOAD_TRACK_COORDINATE_FEATURES_SUCCESS,
  LOAD_TRACK_EXPRESSION_VALUES_FAILURE,
  LOAD_TRACK_EXPRESSION_VALUES_REQUEST,
  LOAD_TRACK_EXPRESSION_VALUES_SUCCESS,
  LOAD_TRACK_FEATURES_FAILURE,
  LOAD_TRACK_FEATURES_REQUEST,
  LOAD_TRACK_FEATURES_SUCCESS,
  LOAD_TRACK_PHENOTYPE_VALUES_FAILURE,
  LOAD_TRACK_PHENOTYPE_VALUES_REQUEST,
  LOAD_TRACK_PHENOTYPE_VALUES_SUCCESS,
  LOAD_TRACK_VARIANTS_FAILURE,
  LOAD_TRACK_VARIANTS_REQUEST,
  LOAD_TRACK_VARIANTS_SUCCESS,
  LOAD_TRACK_VCF_VARIANTS_FAILURE,
  LOAD_TRACK_VCF_VARIANTS_REQUEST,
  LOAD_TRACK_VCF_VARIANTS_SUCCESS,
  LOAD_VIEW_SUCCESS,
  LOG_APPLICATION_ERROR,
  RESET_GENOME_BROWSER,
  SEARCH_SEQUENCED_GENOMES_FAILURE,
  SEARCH_SEQUENCED_GENOMES_REQUEST,
  SEARCH_SEQUENCED_GENOMES_SUCCESS,
  SELECT_TABLE_ROWS,
  SET_ACTIVE_FEATURES_TABLE,
  SET_GB_HIGHLIGHT_COLOR_SCHEME,
  SET_SELECTED_MUTANT_GENOME_LIST,
  SET_SELECTED_RANGE,
  SET_TRACK_CUSTOMIZATION,
  SET_TRACK_LIST,
  SHOW_ALL_FEATURES_STATE_CHANGED,
  VIEW_RANGE_CHANGED,
} from "@/redux/action-types.ts";
import { TRACK_HEADER_DEFAULT_WIDTH } from "@/settings/genome-viewer-settings.ts";
import {
  DATATABLE_PARENT_TYPE,
  ENTITY_TYPE,
  FEATURE_TABLE_DEFAULT_HEIGHT,
  FEATURE_TABLE_DEFAULT_WIDTH,
  GENOME_VIEWER_MUTANT_GENOMES_COUNT_THRESHOLD,
  MAX_FEATURES_FOR_FEATURE_LOADING,
  MIN_RANGE_WIDTH_FOR_AAFEATURE_LOADING,
  MIN_RANGE_WIDTH_FOR_MARKER_LOADING,
  MIN_RANGE_WIDTH_FOR_NUFEATURE_LOADING,
  MIN_RANGE_WIDTH_FOR_STANDARD_FEATURE_LOADING,
  MIN_VIEW_RANGE_SIZE,
  PHENOTYPE_TYPE,
  TRACK_SUBTYPE,
  TRACK_TYPE,
  TableRestriction,
  UNKNOWN_GENOME_ID,
  VIEW,
} from "@/settings/settings.ts";
import {
  type ICoordinateTrackData,
  INITIAL_COORDINATE_TRACK_DATA,
} from "@/ui-modules/genome-viewer/coordinate-track/CoordinateTrackLoader.tsx";

/**
 * The general mode of action of the genome browser module is as follows:
 *
 * - The INIT_GENOME_BROWSER action triggers the loading of the genome with all of its contigs including initialization
 *   of the TrackDataMap
 * - Actions VIEW_WIDTH_CHANGED, VIEW_RANGE_CHANGED, CONTIG_CHANGED for updating visible range and layout
 * - Changes to the contig or range will trigger LOAD_TRACK_FEATURES_REQUEST actions for each track to load new data
 *   from the server. The query range for data loading is the visible range extended by RANGE_PREFETCH_FRACTION to
 *   each side in order to prefetch some data for better browsing behaviour.
 */

export const NGS_PILEUP_RANGE_DENSITY = 1 / 10;

// ad-hoc flag track to be added to the list that maps to the visible features tables (it is not included in the tracks, but controlled by a showFlag boolean)
export const ARTIFICIAL_FLAGS_TRACK: ISortableTrack = {
  name: "Genome Flags",
  type: TRACK_TYPE.REGION_FLAG,
  hidden: false,
  hideCheckboxes: true,
  subTypeId: undefined,
  trackCustomization: undefined,
  pinned: null,
};

export type IGenericVariant = Variant | VcfVariant;

/**
 * State definition
 */
export interface IGenomeViewerStateLayout {
  viewWidth: number; // the total width of the genome browser container div (continuously measured)
  viewHeight: number; // the total height of the genome browser container div (continuously measured)
  headerWidth: number;
  trackWidth: number; // viewWidth - headerWidth (calculated for convenience)
}

export interface IGenomeBrowserState {
  isLoading: boolean; // flag indicating if the genome currently being loaded (loading of tracks: flag in each of the ISortableTrack)
  genome: Organism; // the genome to be browsed including all of its contigs
  statistics: GenomeBrowserStatistics; // for determining the available tracks
  contig: Contig; // the currently visible contig
  range: Range; // the currently visible range in bp coordinates [start, stop] with stop including
  selectedRange: Range; // the currently selected  range (via ctrl-drag), may be null if no range is currently selected
  tracks: ISortableTrack[]; // track specifications including name and feature type of tracks to be shown
  trackDataMap: ITrackDataMap; // map of currently loaded data for each track (keyed by trackId)
  selectedMutantGenomes: IMutantGenome[]; // selected list of mutant genomes (does not include the reference genome)
  flags: RegionFlag[]; // list of visible region flags of the current contig
  featuresTableLayout: SplitPaneLayoutState;
  colorSchemes: IColorSchemes;
  expressionColoringDatasets: Record<number, IExpressionColoringDatasetLite>;
  bamDatafiles: Record<number, BamSummary>;
  bedDatafiles: Record<number, ProjectFile>;
  coordinateLists: Record<number, CoordinateList>;
  activeFeaturesTable: TableQuerySpec;
  layout: IGenomeViewerStateLayout;
  isLoadingApplicableOrgPropertyTypes: boolean; // to PhenotypeSelector state ?
  applicableOrgPropertyTypes: GenomicFeature[]; // to PhenotypeSelector state ?
  phenotypeValues: Dictionary<Phenotype[]>; // keyed by org id
  highlightedTrackFeature: { id: number; tableQuerySpec: InputTableQuerySpec };
}

/**
 * Interfaces
 */

export interface IConfiguredPhenotype extends GenomicFeature, TrackPhenotypeConfiguration {}

export interface IExpressionColoringDatasetLite {
  dataset: Dataset; // dataset selected in one single track (might have one different dataset for each track)
  isLoading: boolean;
  conditionNamesLookup: IConditionNameLookup;
}

export type ITrackDataMap = Record<string, ITrackData>;

export enum PIN_TRACK {
  TOP = "top",
  BOTTOM = "bottom",
}

export interface ISortableTrack extends Track {
  hidden?: boolean;
  hideCheckboxes?: boolean;
  color?: string;
}

export interface IMutantGenome extends Pick<Organism, "id" | "name"> {
  highlighted?: boolean;
}

export interface INgsCoverageRangeLimits {
  // Summarizes the smallest and highest values included in a ngs coverage raster.
  // The minimium value for dynamic linear scaling is always 0, therefore we do not need to store it here.
  minLog: number; // the minimum raster value used for dynamic log scaling (it's the smallest value of the raster above 0)
  max: number; // the maximum raster value used for both dynamic linear and dynamic log scaling
}

export interface ITrackData extends ITrackIdentification, Track {
  //subTypeId: TRACK_SUBTYPE; // redefine the type from "any"
  isLoading: boolean;
  contig: Contig; // contig of features
  range: Range; // query range of features (may be larger than visible range due to pre-fetching)
  message: string;
  //trackId: string;
}

// properties which allows to uniquely identify a track
export interface ITrackIdentification {
  type: string;
  subTypeId: any;
  trackSubtype?: TRACK_SUBTYPE;
  fileId?: number;
  coordinateListId?: number;
}

/**
 * Initial data and constants
 */
// regular genome browser tracks, all hidden by default: the visibility of the track is defined by the GenomeView input props
export const genomeBrowserDefaultTracks: Array<Partial<ISortableTrack>> = [
  { name: "Sequence", type: TRACK_TYPE.SEQUENCE, hidden: true, pinned: PIN_TRACK.TOP },
  { name: "Gene", type: TRACK_TYPE.GENE, hidden: true },
  { name: "Protein", type: TRACK_TYPE.PROTEIN_LOCATION, hidden: true },
  { name: "Transcript", type: TRACK_TYPE.TRANSCRIPT_LOCATION, hidden: true },
  { name: "Derived Variants", type: TRACK_TYPE.VARIANT, hidden: true },
  { name: "Variants", type: TRACK_TYPE.VCF_VARIANT, hidden: true },
  { name: "BLAST HSPs", type: TRACK_TYPE.BLAST_ALIGNMENT, hidden: true },
  {
    name: "NGS",
    type: TRACK_TYPE.NGS,
    hidden: true,
  },
  {
    name: "BED Features",
    type: TRACK_TYPE.BED,
    hidden: true,
  },
  {
    name: "Coordinates",
    type: TRACK_TYPE.COORDINATE,
    hidden: true,
  },
];

const initialState: IGenomeBrowserState = {
  isLoading: false,
  genome: null,
  selectedMutantGenomes: [],
  statistics: null,
  flags: [],
  contig: null,
  range: null,
  selectedRange: null,
  tracks: genomeBrowserDefaultTracks as ISortableTrack[],
  trackDataMap: {},
  featuresTableLayout: {
    hidden: true,
    // size: FEATURE_TABLE_DEFAULT_WIDTH, // we should change the name of this property to featuresTableSize, but need to change the database storage as well
    size: undefined, // => 50% by default
    verticalLayout: true,
  },
  colorSchemes: {
    highlight: null,
  },
  expressionColoringDatasets: {},
  bamDatafiles: {},
  bedDatafiles: {},
  coordinateLists: {},
  activeFeaturesTable: null,
  layout: {
    viewWidth: 0,
    viewHeight: 0,
    headerWidth: TRACK_HEADER_DEFAULT_WIDTH,
    trackWidth: 0,
  },
  isLoadingApplicableOrgPropertyTypes: false,
  applicableOrgPropertyTypes: [],
  phenotypeValues: {},
  highlightedTrackFeature: { id: null, tableQuerySpec: null },
};

const initialTrackData: ITrackData = {
  name: null,
  isLoading: false,
  contig: null,
  range: null,
  trackCustomization: null,
  type: null,
  subTypeId: null,
  pinned: null,
  message: null,
};

/**
 * Reducers
 */

export default createReducer(initialState, {
  [LOAD_VIEW_SUCCESS]: (state: IGenomeBrowserState, view: View): IGenomeBrowserState => {
    if (view.viewType === VIEW.GENOME_VIEW.type) {
      const genomeViewState: GenomeViewState = view.state.genomeView;

      //const tracks = getAllTracks(genomeViewState.tracks, genomeViewState.genome.genomeStatistics);
      const tracks = genomeViewState.tracks.map((t) => ({ ...t, name: t.type })); // full listing of tracks will be created when statistics will have been downloaded(async)
      const trackDataMap: ITrackDataMap = updateTrackDataMap(tracks, {});

      return {
        ...state,
        genome: genomeViewState.genome,
        contig: genomeViewState.contig,
        range: genomeViewState.range,
        tracks: tracks,
        featuresTableLayout: {
          // TODO the view is missing the featuresTableWidth => refact so that the view contains the complete featuresTableLayout
          ...state.featuresTableLayout,
          verticalLayout: genomeViewState.verticalLayout,
          hidden: genomeViewState.hideFeatureTable,
        },
        selectedMutantGenomes: genomeViewState.orderedMutantGenomes.map(organismToMutantGenome),
        trackDataMap: trackDataMap,
        activeFeaturesTable: genomeViewState.tableView?.tableQuerySpec,
        layout: {
          ...state.layout,
          headerWidth: genomeViewState.headerWidth || state.layout.headerWidth,
        },
        // statistics: should be loaded dynamically independently of the saved view
        // for variantExpressionFilters, see table-data.ts => LOAD_VIEW_SUCCESS reducer
        // for refGenomeId, see then after loadView succeeded
      };
    }
    return state;
  },

  [LOAD_GENOME_BROWSER_GENOME_REQUEST]: (state: IGenomeBrowserState): IGenomeBrowserState => {
    return { ...state, isLoading: true, flags: [] };
  },

  [LOAD_GENOME_BROWSER_STATS_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: GenomeBrowserStatistics,
  ): IGenomeBrowserState => {
    return {
      ...state,
      statistics: payload,
    };
  },

  [LOAD_GENOME_BROWSER_GENOME_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Pick<IGenomeBrowserState, "genome" | "contig" | "range">,
  ): IGenomeBrowserState => {
    const { genome, contig } = payload;
    const range = contig && payload.range && ensureSafeContigRange(payload.range, contig.length);

    return {
      ...state,
      isLoading: false,
      genome,
      contig,
      range,
    };
  },

  [LOAD_GENOME_BROWSER_GENOME_FAILURE]: (state: IGenomeBrowserState): IGenomeBrowserState => {
    return {
      ...state,
      isLoading: false,
      genome: null,
    };
  },

  [CLEAR_GENOME_BROWSER_DATA]: (state: IGenomeBrowserState): IGenomeBrowserState => {
    return {
      ...state,
      isLoading: false,
      contig: null,
      range: null,
      genome: null,
      selectedMutantGenomes: [],
      trackDataMap: {},
      layout: {
        ...state.layout,
      },
    };
  },

  [RESET_GENOME_BROWSER]: (state: IGenomeBrowserState): IGenomeBrowserState => {
    return {
      ...initialState,
      layout: {
        ...state.layout,
      },
    };
  },

  [SET_TRACK_LIST]: (state: IGenomeBrowserState, tracks: ISortableTrack[]): IGenomeBrowserState => {
    const trackDataMap: ITrackDataMap = updateTrackDataMap(tracks, state.trackDataMap);
    return {
      ...state,
      tracks: tracks,
      trackDataMap,
    };
  },

  [ADD_BED_FILE]: (state: IGenomeBrowserState, bedFileId: number): IGenomeBrowserState => {
    const newTracks = state.tracks.map((track) =>
      track.type === TRACK_TYPE.BED
        ? {
            ...track,
            hidden: false,
            trackCustomization: {
              ...(track.trackCustomization || {}),
              bedConfiguration: {
                ...(track.trackCustomization?.bedConfiguration || {}),
                orderedBedFiles: [
                  ...(track.trackCustomization?.bedConfiguration?.orderedBedFiles || []),
                  {
                    ...DEFAULT_BED_TRACK_CONFIGURATION,
                    bedFileId: bedFileId,
                  },
                ],
              },
            } as TrackCustomization,
          }
        : track,
    );
    const trackDataMap: ITrackDataMap = updateTrackDataMap(newTracks, state.trackDataMap);
    return {
      ...state,
      tracks: newTracks,
      trackDataMap,
    };
  },

  [SET_SELECTED_MUTANT_GENOME_LIST]: (
    state: IGenomeBrowserState,
    payload: { mutantGenomes: IMutantGenome[] },
  ): IGenomeBrowserState => {
    return {
      ...state,
      selectedMutantGenomes: payload.mutantGenomes,
    };
  },

  [HIGHLIGHT_GENOME_FEATURE]: (
    state: IGenomeBrowserState,
    payload: { id: number; tableQuerySpec: InputTableQuerySpec },
  ): IGenomeBrowserState => {
    return {
      ...state,
      highlightedTrackFeature: payload,
    };
  },

  [FEATURES_TABLE_STATE_CHANGED]: (state: IGenomeBrowserState, payload: SplitPaneLayoutState): IGenomeBrowserState => {
    // if we switch the layout mode, we reset the size of the features table to its default
    if (
      (payload.verticalLayout === true || payload.verticalLayout === false) &&
      payload.verticalLayout !== state.featuresTableLayout.verticalLayout
    ) {
      payload.size = payload.verticalLayout ? FEATURE_TABLE_DEFAULT_HEIGHT : FEATURE_TABLE_DEFAULT_WIDTH;
    }
    return {
      ...state,
      featuresTableLayout: {
        ...state.featuresTableLayout,
        ...payload,
      },
    };
  },

  [GENOME_VIEWER_LAYOUT_CHANGED]: (
    state: IGenomeBrowserState,
    layout: Partial<IGenomeViewerStateLayout>,
  ): IGenomeBrowserState => {
    let trackWidth = state.layout.trackWidth;
    if (layout.viewWidth) {
      trackWidth = layout.viewWidth - state.layout.headerWidth;
    } else if (layout.headerWidth) {
      trackWidth = state.layout.viewWidth - layout.headerWidth;
    }
    return {
      ...state,
      layout: {
        ...state.layout,
        ...layout,
        trackWidth,
      },
    };
  },

  [VIEW_RANGE_CHANGED]: (state: IGenomeBrowserState, range: Range): IGenomeBrowserState => {
    // We can only call ensureSafeContigRange if the contig is already loaded.
    // However, loading a saved genome view requires the range to be applied to redux state even
    // before the contig is available, otherwise we show the default gene range instead of the
    // saved range. Thereofore, if the contig is not there, we put the range into the redux state without
    // further check. See SEL5-1680.
    const safeRange = state.contig
      ? ensureSafeContigRange(
          {
            start: range.start,
            stop: getRangeLength(range) >= MIN_VIEW_RANGE_SIZE ? range.stop : range.start + MIN_VIEW_RANGE_SIZE,
          },
          state.contig.length,
        )
      : range;
    return {
      ...state,
      range: safeRange,
    };
  },

  [CONTIG_CHANGED]: (state: IGenomeBrowserState, contig: Contig): IGenomeBrowserState => {
    return {
      ...state,
      contig,
      range: {
        start: 1,
        stop: contig.length,
      },
      trackDataMap: clearAllTrackData(state.trackDataMap),
    };
  },

  [CONTIG_AND_RANGE_CHANGED]: (state: IGenomeBrowserState, payload: any): IGenomeBrowserState => {
    const newRange: Range = ensureSafeContigRange(payload.range, payload.contig.length);
    if (state.range === null || !rangeEquals(newRange, state.range) || !isEqual(payload.contig, state.contig)) {
      return {
        ...state,
        contig: payload.contig,
        range: newRange,
        trackDataMap: clearAllTrackData(state.trackDataMap),
      };
    } else {
      return state;
    }
  },

  [SET_SELECTED_RANGE]: (state: IGenomeBrowserState, range: Range): IGenomeBrowserState => {
    const selectedRange = range ? ensureSafeContigRange(range, state.contig.length) : null;
    return {
      ...state,
      selectedRange: selectedRange,
    };
  },

  [LOAD_TRACK_FEATURES_REQUEST]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_TRACK_BED_FEATURES_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_TRACK_COORDINATE_FEATURES_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_TRACK_VARIANTS_REQUEST]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_TRACK_VCF_VARIANTS_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_PROFILE_FEATURES_REQUEST]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_NGS_COVERAGE_FEATURES_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_TRACK_EXPRESSION_VALUES_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_TRACK_COMMUNITY_ANNOTATIONS_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_APPLICABLE_ORG_PROPERTY_TYPES_REQUEST]: (state: IGenomeBrowserState): IGenomeBrowserState => {
    return { ...state, isLoadingApplicableOrgPropertyTypes: true };
  },

  [LOAD_APPLICABLE_ORG_PROPERTY_TYPES_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: GenomicFeature[],
  ): IGenomeBrowserState => {
    return { ...state, isLoadingApplicableOrgPropertyTypes: false, applicableOrgPropertyTypes: payload };
  },

  [LOAD_APPLICABLE_ORG_PROPERTY_TYPES_FAILURE]: (state: IGenomeBrowserState): IGenomeBrowserState => {
    return { ...state, isLoadingApplicableOrgPropertyTypes: false };
  },

  [LOAD_TRACK_COMMUNITY_ANNOTATIONS_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    const trackId: string = getTrackId(payload);
    const trackData: IFeatureTrackData = state.trackDataMap[trackId] as IFeatureTrackData;
    // we need to use the existing trackData in order to include the currently visible range, so that we pass the check inside setTrackData
    // TODO bypass the range verification by using a flag? (e.g. for additional annotations like group indicators or expression values)
    return setTrackData(state, {
      ...trackData,
      ...payload,
    });
  },

  [LOAD_TRACK_EXPRESSION_VALUES_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<IFeatureTrackData>,
  ): IGenomeBrowserState => {
    const trackId: string = getTrackId(payload);
    const trackData: IFeatureTrackData = state.trackDataMap[trackId] as IFeatureTrackData;
    let updatedFeatures: Feature[] = trackData.features;
    // merge with existing features (alternative could be to store expression values in its own array
    payload.features.forEach((featureWithExpressionValue: Feature) => {
      const index: number = updatedFeatures.findIndex(
        (feature: Feature) => feature.id === featureWithExpressionValue.id,
      );
      if (index > -1) {
        updatedFeatures = mergeItemToArray(updatedFeatures, featureWithExpressionValue, index);
      }
    });
    return setTrackData(state, {
      ...trackData,
      expressionValuesRange: payload.expressionValuesRange,
      features: updatedFeatures,
    } as ITrackData);
  },

  [LOAD_TRACK_FEATURES_SUCCESS]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_TRACK_BED_FEATURES_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_TRACK_COORDINATE_FEATURES_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_TRACK_VARIANTS_SUCCESS]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_TRACK_VCF_VARIANTS_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_PROFILE_FEATURES_SUCCESS]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_NGS_COVERAGE_FEATURES_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_NGS_PILEUP_FEATURES_REQUEST]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return requestTrackData(state, payload);
  },

  [LOAD_NGS_PILEUP_FEATURES_SUCCESS]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_NGS_PILEUP_FEATURES_FAILURE]: (
    state: IGenomeBrowserState,
    payload: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_NGS_READ_TOOLTIP_REQUEST]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return requestTrackData(state, payload, true);
  },

  [LOAD_NGS_READ_TOOLTIP_SUCCESS]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_NGS_READ_TOOLTIP_FAILURE]: (state: IGenomeBrowserState, payload: Partial<ITrackData>): IGenomeBrowserState => {
    return setTrackData(state, payload);
  },

  [LOAD_TRACK_FEATURES_FAILURE]: (state: IGenomeBrowserState, trackData: Partial<ITrackData>): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [LOAD_TRACK_BED_FEATURES_FAILURE]: (
    state: IGenomeBrowserState,
    trackData: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [LOAD_TRACK_COORDINATE_FEATURES_FAILURE]: (
    state: IGenomeBrowserState,
    trackData: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [LOAD_TRACK_VARIANTS_FAILURE]: (state: IGenomeBrowserState, trackData: Partial<ITrackData>): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [LOAD_TRACK_VCF_VARIANTS_FAILURE]: (
    state: IGenomeBrowserState,
    trackData: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [LOAD_PROFILE_FEATURES_FAILURE]: (
    state: IGenomeBrowserState,
    trackData: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [LOAD_NGS_COVERAGE_FEATURES_FAILURE]: (
    state: IGenomeBrowserState,
    trackData: Partial<ITrackData>,
  ): IGenomeBrowserState => {
    return resetTrackData(state, trackData);
  },

  [SHOW_ALL_FEATURES_STATE_CHANGED]: (state: IGenomeBrowserState, track: ITrackIdentification): IGenomeBrowserState => {
    const newTrackDataMap: ITrackDataMap = { ...state.trackDataMap };

    if (state.trackDataMap) {
      // can be null because of endpoint errors
      const trackId: string = getTrackId(track);
      const currentTrackData: IFeatureTrackData = state.trackDataMap[trackId] as IFeatureTrackData;
      newTrackDataMap[trackId] = {
        ...currentTrackData,
        showAllFeatures: !currentTrackData.showAllFeatures,
      } as ITrackData;
    }

    return { ...state, trackDataMap: newTrackDataMap };
  },

  [HIGHLIGHT_MUTANT_GENOME]: (state: IGenomeBrowserState, genomeId: number): IGenomeBrowserState => {
    return {
      ...state,
      selectedMutantGenomes: state.selectedMutantGenomes.map((mutantGenome: IMutantGenome) => {
        return { ...mutantGenome, highlighted: !mutantGenome.highlighted && mutantGenome.id === genomeId };
      }),
    };
  },

  [SET_GB_HIGHLIGHT_COLOR_SCHEME]: (
    state: IGenomeBrowserState,
    highlight: IColorSchemeForType,
  ): IGenomeBrowserState => {
    return { ...state, colorSchemes: { highlight } };
  },

  [SET_TRACK_CUSTOMIZATION]: (
    state: IGenomeBrowserState,
    payload: { trackType: string; trackCustomization: Partial<TrackCustomization> },
  ): IGenomeBrowserState => {
    const { trackType, trackCustomization } = payload;
    const updatedTracks: ISortableTrack[] = state.tracks.map((track: ISortableTrack) => {
      return track.type === trackType
        ? { ...track, trackCustomization: { ...track.trackCustomization, ...trackCustomization } }
        : track;
    });
    return {
      ...state,
      tracks: updatedTracks,
    };
  },

  [LOAD_DATASET_FOR_TRACK_COLORING_REQUEST]: (state: IGenomeBrowserState, datasetId: number): IGenomeBrowserState => {
    const existingDatasetState = state.expressionColoringDatasets[datasetId] || {};

    return {
      ...state,
      expressionColoringDatasets: {
        ...state.expressionColoringDatasets,
        [datasetId]: {
          dataset: null,
          conditionNamesLookup: {},
          ...existingDatasetState, // e.g. when opening the coloring customization form, this same dataset is reloaded, and the related data should stay
          isLoading: true,
        },
      },
    };
  },

  [LOAD_DATASET_FOR_TRACK_COLORING_SUCCESS]: (state: IGenomeBrowserState, dataset: Dataset): IGenomeBrowserState => {
    return {
      ...state,
      expressionColoringDatasets: {
        ...state.expressionColoringDatasets,
        [dataset.id]: {
          isLoading: false,
          dataset: dataset,
          conditionNamesLookup: createExpressionColoringConditionNameLookup(dataset),
        },
      },
    };
  },

  [LOAD_DATASET_FOR_TRACK_COLORING_FAILURE]: (state: IGenomeBrowserState, datasetId: number): IGenomeBrowserState => {
    return {
      ...state,
      // if the loading of the dataset did fail (e.g. resurrecting a saved view) then we also cleanup the tracks configuration
      tracks: state.tracks.map((track: Track) =>
        track.trackCustomization?.expressionColoring?.datasetId === datasetId
          ? { ...track, trackCustomization: null }
          : track,
      ),
      expressionColoringDatasets: {
        ...state.expressionColoringDatasets,
        [datasetId]: {
          isLoading: false,
          dataset: null,
          conditionNamesLookup: {},
        },
      },
    };
  },

  [SET_ACTIVE_FEATURES_TABLE]: (state: IGenomeBrowserState, querySpec: TableQuerySpec): IGenomeBrowserState => {
    return {
      ...state,
      activeFeaturesTable: querySpec,
    };
  },

  [LOAD_BAM_FILE_SUMMARY_SUCCESS]: (state: IGenomeBrowserState, bamSummary: BamSummary): IGenomeBrowserState => {
    return {
      ...state,
      bamDatafiles: {
        ...state.bamDatafiles,
        [bamSummary.id]: bamSummary,
      },
    };
  },

  [LOAD_BED_FILE_SUCCESS]: (state: IGenomeBrowserState, bedFile: ProjectFile): IGenomeBrowserState => {
    return {
      ...state,
      bedDatafiles: {
        ...state.bedDatafiles,
        [bedFile.id]: bedFile,
      },
    };
  },
  [LOAD_BED_FILE_FAILURE]: (state: IGenomeBrowserState, payload: { fileId: number }): IGenomeBrowserState => {
    // remove the inaccessible file from the bed track config
    const updatedTracks: ISortableTrack[] = state.tracks.map((track: ISortableTrack) => {
      return track.type === TRACK_TYPE.BED
        ? {
            ...track,
            trackCustomization: {
              ...track.trackCustomization,
              bedConfiguration: {
                ...track.trackCustomization.bedConfiguration,
                orderedBedFiles: track.trackCustomization.bedConfiguration.orderedBedFiles.filter(
                  (bedFile) => bedFile.bedFileId !== payload.fileId,
                ),
              },
            },
          }
        : track;
    });

    return {
      ...state,
      tracks: updatedTracks,
    };
  },
  [LOAD_COORDINATE_LIST_SUCCESS]: (state: IGenomeBrowserState, coordinateList: CoordinateList): IGenomeBrowserState => {
    return {
      ...state,
      coordinateLists: {
        ...state.coordinateLists,
        [coordinateList.id]: coordinateList,
      },
    };
  },
  [LOAD_COORDINATE_LIST_FAILURE]: (
    state: IGenomeBrowserState,
    payload: { coordinateListId: number },
  ): IGenomeBrowserState => {
    // remove the inaccessible file from the bed track config
    const updatedTracks: ISortableTrack[] = state.tracks.map((track: ISortableTrack) => {
      return track.type === TRACK_TYPE.COORDINATE
        ? {
            ...track,
            trackCustomization: {
              ...track.trackCustomization,
              coordinateConfiguration: {
                ...track.trackCustomization.coordinateConfiguration,
                orderedCoordinateLists: track.trackCustomization.coordinateConfiguration.orderedCoordinateLists.filter(
                  (coordinateList) => coordinateList.coordinateListId !== payload.coordinateListId,
                ),
              },
            },
          }
        : track;
    });

    return {
      ...state,
      tracks: updatedTracks,
    };
  },

  [LOAD_BAM_FILE_SUMMARY_FAILURE]: (state: IGenomeBrowserState, payload: { fileId: number }): IGenomeBrowserState => {
    // remove the inaccessible file from the ngs track config
    const updatedTracks: ISortableTrack[] = state.tracks.map((track: ISortableTrack) => {
      return track.type === TRACK_TYPE.NGS
        ? {
            ...track,
            trackCustomization: {
              ...track.trackCustomization,
              ngsConfiguration: {
                ...track.trackCustomization.ngsConfiguration,
                orderedBamFiles: track.trackCustomization.ngsConfiguration.orderedBamFiles.filter(
                  (bamFile) => bamFile.bamFileId !== payload.fileId,
                ),
              },
            },
          }
        : track;
    });

    return {
      ...state,
      tracks: updatedTracks,
    };
  },

  [LOAD_TRACK_PHENOTYPE_VALUES_SUCCESS]: (
    state: IGenomeBrowserState,
    organismPhenotypes: OrganismPhenotype[],
  ): IGenomeBrowserState => {
    // key all values by organism id
    const valuesGroupedByOrganism: Dictionary<Phenotype[]> = {};
    organismPhenotypes.forEach((organismPhenotype: OrganismPhenotype) => {
      valuesGroupedByOrganism[organismPhenotype.organism.id] = organismPhenotype.phenotypeValues;
    });
    return {
      ...state,
      phenotypeValues: valuesGroupedByOrganism,
    };
  },
});

/**
 * Action creators
 */
function updateTrackDataMap(tracks: ISortableTrack[], trackDataMap: ITrackDataMap): ITrackDataMap {
  const newTrackDataMap: ITrackDataMap = { ...trackDataMap };

  tracks
    .filter((track) => !track.hidden)
    .forEach((track) => {
      if (track.type === TRACK_TYPE.NGS) {
        track.trackCustomization?.ngsConfiguration?.orderedBamFiles?.forEach((bamFileConfig: { bamFileId: number }) => {
          let trackId: string = getTrackId({
            ...track,
            fileId: bamFileConfig.bamFileId,
            trackSubtype: TRACK_SUBTYPE.NGS_COVERAGE,
          });
          if (!newTrackDataMap[trackId]) {
            newTrackDataMap[trackId] = {
              ...initialTrackData,
              ...INITIAL_NGS_COVERAGE_TRACK_DATA,
              //bamFileId: bamFileConfig.bamFileId,
              name: track.name,
              type: track.type,
              subTypeId: TRACK_SUBTYPE.NGS_COVERAGE,
            };
          }
          trackId = getTrackId({ ...track, fileId: bamFileConfig.bamFileId, trackSubtype: TRACK_SUBTYPE.NGS_PILEUP });
          if (!newTrackDataMap[trackId]) {
            newTrackDataMap[trackId] = {
              ...initialTrackData,
              ...INITIAL_NGS_READS_TRACK_DATA,
              //bamFileId: bamFileConfig.bamFileId,
              name: track.name,
              type: track.type,
              subTypeId: TRACK_SUBTYPE.NGS_PILEUP,
            };
          }
        });
      } else if (track.type === TRACK_TYPE.BED) {
        track.trackCustomization?.bedConfiguration?.orderedBedFiles?.forEach((bedFileConfig: { bedFileId: number }) => {
          const trackId = getTrackId({ ...track, fileId: bedFileConfig.bedFileId });
          if (!newTrackDataMap[trackId]) {
            newTrackDataMap[trackId] = {
              ...initialTrackData,
              ...INITIAL_BED_TRACK_DATA,
              //bamFileId: bamFileConfig.bamFileId,
              name: track.name,
              type: track.type,
            };
          }
        });
      } else if (track.type === TRACK_TYPE.COORDINATE) {
        track.trackCustomization?.coordinateConfiguration?.orderedCoordinateLists?.forEach(
          (coordinateListConfig: { coordinateListId: number }) => {
            const trackId = getTrackId({ ...track, coordinateListId: coordinateListConfig.coordinateListId });
            if (!newTrackDataMap[trackId]) {
              newTrackDataMap[trackId] = {
                ...initialTrackData,
                ...INITIAL_COORDINATE_TRACK_DATA,
                //bamFileId: bamFileConfig.bamFileId,
                name: track.name,
                type: track.type,
              };
            }
          },
        );
      } else {
        const trackId: string = getTrackId(track);
        if (!newTrackDataMap[trackId]) {
          // if is not loaded yet
          newTrackDataMap[trackId] = {
            ...initialTrackData,
            ...getInitialTrackData(track),
            name: track.name,
            type: track.type,
          };
        }
      }
    });

  return newTrackDataMap;
}

/**
 * Action creators
 */

export function clearGenomeBrowserData(): ReduxAction<void> {
  return (dispatch) => {
    dispatch({
      type: CLEAR_GENOME_BROWSER_DATA,
    });
  };
}

export function resetGenomeBrowser(): ReduxAction<void> {
  return (dispatch) => {
    dispatch({
      type: RESET_GENOME_BROWSER,
    });
  };
}

export function fetchGenomeBrowserStatistics(genomeId: number): ReduxAction<Promise<GenomeBrowserStatistics>> {
  return (dispatch, _getState, webApiClient) => {
    const organism = genomeId !== UNKNOWN_GENOME_ID ? genomeId : null;
    return webApiClient
      .fetchGenomeAndSequenceStatistics(organism)
      .then((stats: GenomeBrowserStatistics) => {
        dispatch({
          type: LOAD_GENOME_BROWSER_STATS_SUCCESS,
          payload: stats || [],
        });
        return stats;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GENOME_BROWSER_STATS_FAILURE,
          alertTitle: "Fetching of Genome Viewer Statistics Failed",
        }),
      );
  };
}

export function initGenomeBrowser(genomeId: number, contigId?: number, range?: Range): ReduxAction<Promise<void>> {
  return (dispatch, getState, webApiClient) => {
    const genomeBrowser: IGenomeBrowserState = getState().genomeBrowser;

    // The Genome Viewer is already loaded, so we do nothing
    if (
      genomeBrowser.genome?.id === genomeId &&
      contigId &&
      genomeBrowser.contig?.id === contigId &&
      range &&
      genomeBrowser.range &&
      rangeEquals(genomeBrowser.range, range)
    ) {
      return Promise.resolve();
    }

    // Set loading to true, remove flags
    dispatch({ type: LOAD_GENOME_BROWSER_GENOME_REQUEST });

    // Loading all the tracks
    return Promise.all([
      genomeId !== UNKNOWN_GENOME_ID
        ? webApiClient.fetchGenomeBrowserGenome(genomeId)
        : Promise.resolve({ id: UNKNOWN_GENOME_ID } as Organism),
      dispatch(loadContig(contigId)),
    ])
      .then(([g, contig]) => {
        const genome: Organism = g;
        const safeRange =
          range && contig ? getPaddedRange(range, contig.length) : (range ?? { start: 1, stop: contig?.length ?? 1 });

        // Just save data to redux
        dispatch({
          type: LOAD_GENOME_BROWSER_GENOME_SUCCESS,
          payload: {
            genome,
            contig: contig,
            range: safeRange,
          },
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GENOME_BROWSER_GENOME_FAILURE,
          alertTitle: "Fetching of Genome Failed",
        }),
      );
  };
}

export function loadContig(contigId?: number): ReduxAction<Promise<null | Contig>> {
  return (dispatch, getState: () => IAppState, webApiClient) => {
    if (!contigId) {
      return Promise.resolve(null);
    }
    const contig = getState().genomeBrowser.contig;
    if (contig?.id === contigId) {
      return Promise.resolve(getState().genomeBrowser.contig);
    }
    return webApiClient.fetchContigForGenomeBrowser(contigId).catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_GENOME_BROWSER_GENOME_FAILURE,
        alertTitle: "Fetching of Contig Failed",
      }),
    );
  };
}

export function viewWidthChanged(viewWidth: number) {
  return {
    type: GENOME_VIEWER_LAYOUT_CHANGED,
    payload: {
      viewWidth,
    } as IGenomeViewerStateLayout,
  };
}

export function viewHeightChanged(viewHeight: number) {
  return {
    type: GENOME_VIEWER_LAYOUT_CHANGED,
    payload: {
      viewHeight,
    } as IGenomeViewerStateLayout,
  };
}

export function trackHeaderWidthChanged(headerWidth: number) {
  return {
    type: GENOME_VIEWER_LAYOUT_CHANGED,
    payload: {
      headerWidth,
    } as IGenomeViewerStateLayout,
  };
}

export function featuresTableHideStateChanged(featuresTableLayout: SplitPaneLayoutState) {
  return {
    type: FEATURES_TABLE_STATE_CHANGED,
    payload: featuresTableLayout,
  };
}

export function showFlagsStateChanged(showFlags: boolean): ReduxAction<void> {
  return (dispatch, getState: () => IAppState) => {
    const params: InputUserParamsInput = { showFlagsTrack: showFlags };
    void dispatch(updateUserParams(params)).then(() => {
      dispatch(automaticallySelectActiveFeaturesTable(getState().genomeBrowser.tracks));
    });
  };
}

export function showAnnotationStateChanged(showCommunityAnnotation: boolean) {
  return (dispatch) => {
    const params: InputUserParamsInput = { showCommunityAnnotations: showCommunityAnnotation };
    dispatch(updateUserParams(params));
  };
}

export function viewRangeChanged(range: Range) {
  return (dispatch, _getState) => {
    if (!range) {
      return;
    }
    dispatch({
      type: VIEW_RANGE_CHANGED,
      payload: range,
    });
  };
}

export function selectRange(range: Range) {
  return (dispatch, _getState) => {
    dispatch({
      type: SET_SELECTED_RANGE,
      payload: range,
    });
  };
}

export function updateSelectionModelFromRange(range: Range) {
  return (dispatch, getState: () => IAppState) => {
    if (!range) {
      return;
    }
    // get which tables might have their selection impacted...
    const state = getState();
    const genomeBrowser: IGenomeBrowserState = state.genomeBrowser;

    const tableSpecs: InputTableQuerySpec[] = flatten(
      getDisplayedTracksWithSelectableFeatures(genomeBrowser.tracks, false).map((track: ISortableTrack) =>
        dispatch(getAllTableSpecsFromTrackUsingGenomeInformation(track)),
      ),
    );

    // ...and for each of these tables
    tableSpecs.forEach((tableSpec: InputTableQuerySpec) => {
      const trackId = getTrackIdFromTableSpec(tableSpec);
      const trackData: ITrackData = genomeBrowser.trackDataMap[trackId];

      // ...identify the items that intersect with the selection rectangle
      let selectionSet: TGridSelModelIdentifier[] = [];
      switch (trackData?.type) {
        case TRACK_TYPE.VARIANT:
        case TRACK_TYPE.VCF_VARIANT:
          selectionSet = (trackData as IVariantTrackData)?.variants
            ?.filter((item: Variant) => rangesIntersect(item.region.range, range))
            .map((item: Variant) => item.id);
          break;
        default:
          selectionSet = (trackData as IFeatureTrackData)?.features
            ?.filter((item: Feature) => rangesIntersect(item.region.range, range))
            .map((item: Feature) => item.id);
          break;
      }
      // ...push a selection action for each single table
      dispatch({
        type: SELECT_TABLE_ROWS,
        payload: {
          tableSpec: tableSpec,
          ids: selectionSet || [],
          selected: true,
        },
      });
    });
  };
}

export function contigChanged(contig: Contig) {
  return (dispatch, getState: () => IAppState) => {
    const selectedContig = getState().genomeBrowser.contig;
    if (selectedContig?.id === contig.id) {
      return;
    }
    return dispatch(loadContig(contig.id)).then((returnedContig: Contig) => {
      dispatch({
        type: CONTIG_CHANGED,
        payload: returnedContig,
      });
    });
  };
}

// TODO merge contigChanged & contigAndRangeChanged
export function contigAndRangeChanged(contig: Contig, range: Range) {
  return (dispatch): Promise<any> => {
    return dispatch(loadContig(contig.id)).then((returnedContig: Contig) => {
      dispatch({
        type: CONTIG_AND_RANGE_CHANGED,
        payload: {
          contig: returnedContig,
          range: range,
        },
      });
    });
  };
}

// currently only used for inferring the global isLoading flag
export function getAllVisibleTrackIds(tracks: ISortableTrack[]): string[] {
  const visibleTrackIds: string[] = [];
  tracks.forEach((track: ISortableTrack) => {
    if (track.type === TRACK_TYPE.NGS) {
      track.trackCustomization?.ngsConfiguration?.orderedBamFiles?.forEach(
        (bamFileConfig: TrackNgsOrderedBamFilesData) => {
          visibleTrackIds.push(
            getTrackId({ ...track, fileId: bamFileConfig.bamFileId, trackSubtype: TRACK_SUBTYPE.NGS_COVERAGE }),
          );
          if (bamFileConfig.visiblePileup) {
            visibleTrackIds.push(
              getTrackId({ ...track, fileId: bamFileConfig.bamFileId, trackSubtype: TRACK_SUBTYPE.NGS_PILEUP }),
            );
          }
        },
      );
    } else if (track.type === TRACK_TYPE.BED) {
      track.trackCustomization?.bedConfiguration?.orderedBedFiles?.forEach((bedFileConfig: TrackBedOrderedBedFile) => {
        visibleTrackIds.push(getTrackId({ ...track, fileId: bedFileConfig.bedFileId }));
      });
    } else if (track.type === TRACK_TYPE.COORDINATE) {
      track.trackCustomization?.coordinateConfiguration?.orderedCoordinateLists?.forEach(
        (coordinateConfig: TrackCoordinateOrderedCoordinateList) => {
          visibleTrackIds.push(getTrackId({ ...track, coordinateListId: coordinateConfig.coordinateListId }));
        },
      );
    } else {
      visibleTrackIds.push(getTrackId(track));
    }
  });
  return visibleTrackIds;
}

export function getTrackFeatureTypeCount(track: Track, contig: Contig): number {
  switch (track.type) {
    case TRACK_TYPE.GENE:
      return contig?.contigStatistics?.geneCount;
    case TRACK_TYPE.PROTEIN_LOCATION:
      return contig?.contigStatistics?.proteinsCount;
    case TRACK_TYPE.TRANSCRIPT_LOCATION:
      return contig?.contigStatistics?.transcriptsCount;
    case TRACK_TYPE.NUFEATURE:
      const nuFeatureType: NuFeatureStats = contig?.contigStatistics?.nuFeaturesCount?.find(
        (n: NuFeatureStats) => n.featureTypeId === track.subTypeId,
      );
      return nuFeatureType?.count;
    case TRACK_TYPE.AAFEATURE:
      const aaFeatureType: AaFeatureStats = contig?.contigStatistics?.aaFeaturesCount?.find(
        (n: AaFeatureStats) => n.featureTypeId === track.subTypeId,
      );
      return aaFeatureType?.count;
    case TRACK_TYPE.MARKER_NUS:
      const markerType: MarkerStats = contig?.contigStatistics?.markersCount?.find(
        (m: MarkerStats) => m.markerTypeId === track.subTypeId,
      );
      return markerType?.count;
    default:
      return 0;
  }
}

export function featureDensityOk(track: Track, contig: Contig, visibleStart: number, visibleStop: number) {
  const visibleRangeLength: number = visibleStop - visibleStart + 1;

  // if we are zoomed in sufficiently, always show the features
  let rangeWidthLimit: number;
  switch (track.type) {
    case TRACK_TYPE.NUFEATURE:
      rangeWidthLimit = MIN_RANGE_WIDTH_FOR_NUFEATURE_LOADING;
      break;
    case TRACK_TYPE.AAFEATURE:
      rangeWidthLimit = MIN_RANGE_WIDTH_FOR_AAFEATURE_LOADING;
      break;
    case TRACK_TYPE.MARKER_NUS:
      rangeWidthLimit = MIN_RANGE_WIDTH_FOR_MARKER_LOADING;
      break;
    default:
      rangeWidthLimit = rangeWidthLimit = MIN_RANGE_WIDTH_FOR_STANDARD_FEATURE_LOADING;
  }
  if (visibleRangeLength < rangeWidthLimit) {
    return true;
  }

  // otherwise calculate the expected number of features in the visible region and decide upon it

  const featureTypeCount: number = getTrackFeatureTypeCount(track, contig);

  // if we can't calculate the feature density, do not show the features
  if (featureTypeCount == null) {
    return false;
  }

  if (featureTypeCount === 0) {
    return true;
  }

  return (featureTypeCount / contig.length) * visibleRangeLength < MAX_FEATURES_FOR_FEATURE_LOADING;
}

export function getRangeDensityPerScreenPixel(range: Range, width: number, maxPixelRatio = 2): number {
  // 1 is the minimal acceptable value.
  if (range == undefined || width == undefined) return 1;

  // We should not go with a pixel ration bigger than 2 for performance purpose.
  const currentScreenDensity = Math.min(window.devicePixelRatio || 1, maxPixelRatio);
  const rangeLength = Math.abs(range.stop - range.start);

  return isNaN(rangeLength) ? 1 : (width * currentScreenDensity) / rangeLength;
}

export function trackCustomizationChanged(oldCustomization: TrackCustomization, newCustomization: TrackCustomization) {
  return (
    // no strict comparison here because null == undefined should return true
    oldCustomization != newCustomization
  );
}

export function setTrackList(orderedTracks: ISortableTrack[]) {
  return (dispatch: ReduxDispatch, _getState: () => IAppState) => {
    dispatch(automaticallySelectActiveFeaturesTable(orderedTracks));

    dispatch({
      type: SET_TRACK_LIST,
      payload: orderedTracks,
    });
  };
}

export function automaticallySelectActiveFeaturesTable(tracks: ISortableTrack[]) {
  return (dispatch: ReduxDispatch, getState: () => IAppState) => {
    const activeTableSpec = getState().genomeBrowser.activeFeaturesTable;
    const showFlags = getState().auth.session?.params.showFlagsTrack;

    if (tracks.length) {
      // get list of tracks that will have one or more corresponding tables
      const tableSpecs = flatten(
        getDisplayedTracksWithSelectableFeatures(tracks, showFlags).map((track) => {
          // do not filter the table specs in case the metadata is not yet there
          return dispatch(getAllTableSpecsFromTrackUsingGenomeInformation(track, false));
        }),
      );
      // make sure the currently active tab is part of the list, otherwise...
      if (!activeTableSpec || !tableSpecs.find((tableSpec) => sameTableSpec(tableSpec, activeTableSpec))) {
        // ...automatically choose the active table
        dispatch(selectActiveFeaturesTable(tableSpecs[tableSpecs.length - 1]));
      }
    } else {
      dispatch(selectActiveFeaturesTable(null));
    }
  };
}
export function setSelectedMutantGenomeList(orderedMutantgenomes: Array<IMutantGenome | undefined>) {
  return (dispatch) => {
    dispatch({
      type: SET_SELECTED_MUTANT_GENOME_LIST,
      payload: { mutantGenomes: orderedMutantgenomes },
    });
  };
}

export function addBedFile(bedFileId: number) {
  return {
    type: ADD_BED_FILE,
    payload: bedFileId,
  };
}

export function highlightGenomeFeature(id: number, tableQuerySpec: InputTableQuerySpec) {
  return {
    type: HIGHLIGHT_GENOME_FEATURE,
    payload: { id, tableQuerySpec },
  };
}

export function zoomToRegion(region: Region) {
  return (dispatch) => {
    if (region?.range && region?.contig) {
      dispatch(contigAndRangeChanged(region.contig, getPaddedRange(region.range, region.contig.length)));
    }
  };
}

export function highlightMutantGenome(genomeId: number) {
  return (dispatch, getState: () => IAppState) => {
    // when highlighting a mutant genome from the variant track header, we trigger the scroll to the corresponding entity in the features table

    // there is only one single variant analysis group
    const variantAnalysisGroup = getState().genomeBrowser.genome.variantAnalysisGroups?.[0];

    const mutantGenomesTableSpec: InputTableQuerySpec = createTableSpecForAnalysisGroupParent(variantAnalysisGroup);
    dispatch(ensureRowIsVisible(mutantGenomesTableSpec, genomeId));

    dispatch({
      type: HIGHLIGHT_MUTANT_GENOME,
      payload: genomeId,
    });
  };
}

export function clearGenomeBrowserSelection(): ReduxAction<void> {
  return (dispatch, getState) => {
    const tracks = getState().genomeBrowser.tracks;
    const tableSpecsFromSelectionModel: InputTableQuerySpec[] = flatten(
      getDisplayedTracksWithSelectableFeatures(tracks, false).map((track: ISortableTrack) =>
        dispatch(getAllTableSpecsFromTrackUsingGenomeInformation(track)),
      ),
    );

    tableSpecsFromSelectionModel.forEach((tableSpec) => dispatch(toggleSelection(false, tableSpec)));
    dispatch(selectRange(null));
  };
}

export function selectSingleFeature(
  ids: number[],
  tableQuerySpec: InputTableQuerySpec,
  selected: boolean,
  keepSelection: boolean,
) {
  return (dispatch, getState: () => IAppState) => {
    dispatch(selectTableRows(tableQuerySpec, ids, selected, keepSelection));

    if (selected) {
      // make sure we dispatch the SELECT TAB action only when necessary
      const activeFeaturesTableQuerySpec: TableQuerySpec = getState().genomeBrowser.activeFeaturesTable;
      if (!sameTableSpec(activeFeaturesTableQuerySpec, tableQuerySpec)) {
        dispatch(selectActiveFeaturesTable(tableQuerySpec as TableQuerySpec));
      }
      // zoom to the first selected row
      dispatch(ensureRowIsVisible(tableQuerySpec, ids[0]));
    }
  };
}

export function selectActiveFeaturesTable(tableSpec: InputTableQuerySpec) {
  return {
    type: SET_ACTIVE_FEATURES_TABLE,
    payload: tableSpec,
  };
}

export function toggleShowAllTrackFeatures(track: ITrackIdentification) {
  return {
    type: SHOW_ALL_FEATURES_STATE_CHANGED,
    payload: track,
  };
}

export function fetchTrackFeatures(track: ISortableTrack, contig: Contig, queryRange: Range, withSequence: boolean) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackFeatures> => {
    const trackData: Partial<IFeatureTrackData> = {
      name: track.name,
      type: track.type,
      subTypeId: track.subTypeId,
    };

    dispatch({
      type: LOAD_TRACK_FEATURES_REQUEST,
      payload: {
        ...trackData,
        contig: contig,
        range: queryRange,
        trackCustomization: track.trackCustomization,
      },
    });

    return webApiClient
      .fetchTrackFeatures(track.type, contig.id, queryRange, track.subTypeId, withSequence)
      .then((trackFeatures: TrackFeatures) => {
        dispatch({
          type: LOAD_TRACK_FEATURES_SUCCESS,
          payload: {
            ...trackData,
            ...trackFeatures,
            contig: contig,
            range: queryRange,
          },
        });
        return trackFeatures;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_FEATURES_FAILURE,
          actionPayload: trackData,
          alertTitle: `Fetching of Features of Track '${track.type}' Failed`,
        }),
      );
  };
}

export function fetchTrackExpressionValues(track: ISortableTrack, contig: Contig, queryRange: Range) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackFeatures> => {
    const trackSpec: Partial<IFeatureTrackData> = {
      name: track.name,
      type: track.type,
      contig: contig,
      expressionValuesRange: queryRange,
    };

    dispatch({
      type: LOAD_TRACK_EXPRESSION_VALUES_REQUEST,
      payload: trackSpec,
    });
    const datasetId: number = track.trackCustomization?.expressionColoring?.datasetId;

    return webApiClient
      .fetchTrackExpressionValues(track.type, contig.id, queryRange, track.subTypeId, datasetId)
      .then((trackFeatures: TrackFeatures) => {
        const { range: _, ...others } = trackFeatures; // do not overwrite "range" which is dedicated to the features, not the expression
        const trackData: Partial<IFeatureTrackData> = {
          ...others,
          ...trackSpec,
        };
        dispatch({
          type: LOAD_TRACK_EXPRESSION_VALUES_SUCCESS,
          payload: trackData,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_EXPRESSION_VALUES_FAILURE,
          actionPayload: trackSpec,
          alertTitle: `Fetching of Expression Values of Track '${track.type}' Failed`,
        }),
      );
  };
}

export function isSameTrack(track1: Track, track2: Track): boolean {
  return track1.type === track2.type && (!track1.subTypeId || track1.subTypeId === track2.subTypeId);
}

export function refreshTrackCommunityAnnotations(resultType: string, subTypeId: number) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): void => {
    // after a group was saved, refresh the grup indicators of a potential Genome Viewer track that fits the group specs
    const genomeViewerState: IGenomeBrowserState = getState().genomeBrowser;
    const contig: Contig = genomeViewerState.contig;
    const range: Range = genomeViewerState.range;

    const showAnnotations: boolean = getState().auth.session?.params.showCommunityAnnotations;
    const visibleTrack: ISortableTrack = genomeViewerState?.tracks?.find((t: ISortableTrack) => {
      return t.type === resultType && t.subTypeId == subTypeId && !t.hidden;
    });

    if (showAnnotations && visibleTrack && contig && range) {
      const extendedRange = getExtendedRange(contig, range);
      dispatch(fetchTrackCommunityAnnotations(visibleTrack, contig, extendedRange));
    }
  };
}

export function fetchTrackCommunityAnnotations(
  track: ISortableTrack,
  contig: Contig,
  queryRange: Range,
): ReduxAction<Promise<TrackGroupIndicator[]>> {
  return (dispatch, _getState, webApiClient) => {
    const trackSpec: Partial<ITrackCommunityAnnotationsData> = {
      name: track.name,
      type: track.type,
      subTypeId: track.subTypeId,
      contig: contig,
      communityAnnotationsRange: queryRange,
    };

    dispatch({
      type: LOAD_TRACK_COMMUNITY_ANNOTATIONS_REQUEST,
      payload: trackSpec,
    });

    return webApiClient
      .fetchCommunityAnnotationsIndicators(track.type, contig.id, queryRange, track.subTypeId)
      .then((communityAnnotationData) => {
        //const { range: _, ...others } = groupIndicators; // do not overwrite "range" which is dedicated to the features, not the expression

        const communityAnnotationsLookup: Dictionary<Group[]> = {};
        communityAnnotationData.itemGroupIndicatorData?.forEach((groupAnnotation) => {
          communityAnnotationsLookup[groupAnnotation.itemId] = groupAnnotation.groups;
        });
        const trackData: Partial<ITrackCommunityAnnotationsData> = {
          //...others,
          ...trackSpec,
          communityAnnotationsOverflow: communityAnnotationData.overflow,
          communityAnnotationsLookup: communityAnnotationsLookup,
        };
        dispatch({
          type: LOAD_TRACK_COMMUNITY_ANNOTATIONS_SUCCESS,
          payload: trackData,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_COMMUNITY_ANNOTATIONS_FAILURE,
          actionPayload: trackSpec,
          alertTitle: `Fetching of Community Annotations of Track '${track.type}' Failed`,
        }),
      );
  };
}

export function fetchProfileFeatures(track: Track, contig: Contig, queryRange: Range) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackNuProfile> => {
    const trackData: Partial<ITrackData> = {
      name: track.name,
      type: track.type,
      subTypeId: track.subTypeId,
      contig: contig,
      range: queryRange,
    };

    dispatch({
      type: LOAD_PROFILE_FEATURES_REQUEST,
      payload: trackData,
    });

    return webApiClient
      .fetchProfileFeatures(track.subTypeId, contig.id, queryRange)
      .then((profileFeatures: TrackNuProfile) => {
        dispatch({
          type: LOAD_PROFILE_FEATURES_SUCCESS,
          payload: {
            ...trackData,
            profiles: profileFeatures.profiles,
            overflow: profileFeatures.overflow,
          },
        });
        return profileFeatures;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PROFILE_FEATURES_FAILURE,
          actionPayload: trackData,
          alertTitle: `Fetching of Profile of Track '${track.name}' Failed`,
        }),
      );
  };
}

export function fetchNgsCoverageFeatures(
  track: Track,
  contig: Contig,
  queryRange: Range,
  bamFileId: number,
  canvasWidth: number,
) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<CoverageRaster> => {
    const trackData: Partial<INgsCoverageTrackData> = {
      name: track.name,
      type: track.type,
      contig: contig,
      range: queryRange,
      fileId: bamFileId,
      trackSubtype: TRACK_SUBTYPE.NGS_COVERAGE,
    };

    dispatch({
      type: LOAD_NGS_COVERAGE_FEATURES_REQUEST,
      payload: trackData,
    });

    return webApiClient
      .fetchNgsCoverageFeatures(bamFileId, contig.id, queryRange, canvasWidth)
      .then((ngsCoverageRaster: CoverageRaster) => {
        dispatch({
          type: LOAD_NGS_COVERAGE_FEATURES_SUCCESS,
          payload: {
            ...trackData,
            ngsCoverage: ngsCoverageRaster.coverage,
            ngsCoverageRangeLimits: calculateNgsCoverageRangeLimits(ngsCoverageRaster.coverage),
            rasterParams: {
              canvasWidth: canvasWidth,
              range: queryRange,
            },
          },
        });
        return ngsCoverageRaster;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NGS_COVERAGE_FEATURES_FAILURE,
          actionPayload: trackData,
          throwError: true,
          showAlert: false, // we will show the problem in the track directly
          alertTitle: `Fetching of NGS Coverage of Track '${track.name}' Failed`,
        }),
      );
  };
}

export function fetchNgsPileupFeatures(
  trackName: string, // TODO Should not all tracks use trackId instead?
  contig: Contig,
  range: Range,
  bamFileId: number,
  lanesCount: number,
  includeSoftClippedReads: boolean,
): ReduxAction<Promise<PileupRow[]>> {
  return (dispatch, _getState, webApiClient) => {
    const trackData: Partial<INgsReadsTrackData> = {
      name: trackName,
      type: TRACK_TYPE.NGS,
      contig,
      range,
      fileId: bamFileId,
      trackSubtype: TRACK_SUBTYPE.NGS_PILEUP,
      isLoading: true,
    };

    dispatch({
      type: LOAD_NGS_PILEUP_FEATURES_REQUEST,
      payload: trackData,
    });

    return webApiClient
      .fetchNgsPileupFeatures(bamFileId, contig.id, range, includeSoftClippedReads, lanesCount)
      .then((ngsPileupData: PileupRow[]) => {
        dispatch({
          type: LOAD_NGS_PILEUP_FEATURES_SUCCESS,
          payload: {
            ...trackData,
            isLoading: false,
            ngsPileupDataLength: ngsPileupData?.length ?? 0,
          },
        });
        return ngsPileupData;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NGS_PILEUP_FEATURES_FAILURE,
          actionPayload: {
            ...trackData,
            isLoading: false,
          },
          throwError: true,
          showAlert: false, // we will show the corresponding message in the coverage track
          alertTitle: `Fetching of NGS Pileup of Track '${trackName}' Failed`,
        }),
      );
  };
}

export function fetchTrackBedFeatures(track: ISortableTrack, contig: Contig, queryRange: Range, bedFileId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackFeatures> => {
    const trackData: Partial<IBedTrackData> = {
      name: track.name,
      type: track.type,
      fileId: bedFileId,
    };

    dispatch({
      type: LOAD_TRACK_BED_FEATURES_REQUEST,
      payload: {
        ...trackData,
        contig: contig,
        range: queryRange,
        trackCustomization: track.trackCustomization,
      },
    });

    return webApiClient
      .fetchBedFeatures(bedFileId, contig.id, queryRange)
      .then((trackBedFeatures: TrackFeatures) => {
        dispatch({
          type: LOAD_TRACK_BED_FEATURES_SUCCESS,
          payload: {
            ...trackData,
            ...trackBedFeatures,
            contig: contig,
            range: queryRange,
          },
        });
        return trackBedFeatures;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_BED_FEATURES_FAILURE,
          actionPayload: {
            ...trackData,
          },
          throwError: true,
          alertTitle: `Fetching of BED Features of Track '${track.name}' Failed`,
        }),
      );
  };
}

export function fetchTrackCoordinateFeatures(
  track: ISortableTrack,
  contig: Contig,
  queryRange: Range,
  coordinateListId: number,
) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackFeatures> => {
    const trackData: Partial<ICoordinateTrackData> = {
      name: track.name,
      type: track.type,
      coordinateListId: coordinateListId,
    };

    dispatch({
      type: LOAD_TRACK_COORDINATE_FEATURES_REQUEST,
      payload: {
        ...trackData,
        contig: contig,
        range: queryRange,
        trackCustomization: track.trackCustomization,
      },
    });

    return webApiClient
      .fetchCoordinateFeatures(coordinateListId, contig.id, queryRange)
      .then((trackCoordinateFeatures: TrackFeatures) => {
        dispatch({
          type: LOAD_TRACK_COORDINATE_FEATURES_SUCCESS,
          payload: {
            ...trackData,
            ...trackCoordinateFeatures,
            contig: contig,
            range: queryRange,
          },
        });
        return trackCoordinateFeatures;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_COORDINATE_FEATURES_FAILURE,
          actionPayload: {
            ...trackData,
          },
          throwError: true,
          alertTitle: `Fetching of Coordinate Features of Track '${track.name}' Failed`,
        }),
      );
  };
}

export function fetchNgsReadTooltip(
  // for the track
  trackName: string, // TODO Should not all tracks use trackId instead?
  contig: Contig,
  bamFileId: number,
  // for the tooltip
  alignment: PileupAlignment,
  segment: PileupAlignmentSegment,
  //requestUid: string,
  index: number,
): ReduxAction<Promise<INgsReadsTooltip>> {
  return (dispatch, _getState, webApiClient) => {
    const trackData: Partial<INgsReadsTrackData> = {
      name: trackName,
      type: TRACK_TYPE.NGS,
      contig,
      range: {
        start: segment.start,
        stop: segment.stop,
      },
      fileId: bamFileId,
      subTypeId: TRACK_SUBTYPE.NGS_PILEUP,
    };

    return webApiClient
      .fetchNgsReadTooltip(bamFileId, alignment.start, alignment.readName, {
        range: {
          start: segment.start,
          stop: segment.stop,
        },
        contigId: contig.id,
      })
      .then((content) => {
        return {
          index,
          alignment: { ...alignment, ...content },
          segment,
        };
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NGS_READ_TOOLTIP_FAILURE,
          actionPayload: { ...trackData },
          throwError: true,
          alertTitle: `Fetching of NGS Read Content of Track '${trackName}' Failed`,
        }),
      );
  };
}

export interface ITrackVariantInput {
  track: Track;
  contig: Contig;
  queryRange: Range;
  refGenomeId?: number;
  limit: number;
  filterExpression: string;
  mutantGenomeIds: number[];
}

export function fetchTrackVariants(input: ITrackVariantInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackVariants> => {
    const { track, contig, queryRange, refGenomeId, limit, filterExpression, mutantGenomeIds } = input;

    const trackData: Partial<IVariantTrackData> = {
      name: track.name,
      type: track.type,
      contig: contig,
      range: queryRange,
      refGenomeId: refGenomeId,
    };

    dispatch({
      type: LOAD_TRACK_VARIANTS_REQUEST,
      payload: {
        ...trackData,
        filterExpression,
        mutantGenomeIds,
      },
    });
    return webApiClient
      .fetchTrackVariants(contig.id, queryRange, refGenomeId, filterExpression, limit, mutantGenomeIds)
      .then((trackVariants: TrackVariants) => {
        dispatch({
          type: LOAD_TRACK_VARIANTS_SUCCESS,
          payload: {
            ...trackData,
            ...trackVariants,
          },
        });
        return trackVariants;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_VARIANTS_FAILURE,
          actionPayload: trackData,
          alertTitle: `Fetching Data of Variants Track Failed`,
        }),
      );
  };
}

export function fetchTrackVcfVariants(input: ITrackVariantInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<TrackVariants> => {
    const { track, contig, queryRange, refGenomeId, limit, filterExpression, mutantGenomeIds } = input;

    const trackData: Partial<IVariantTrackData> = {
      name: track.name,
      type: track.type,
      contig: contig,
      range: queryRange,
      refGenomeId: refGenomeId,
    };

    dispatch({
      type: LOAD_TRACK_VCF_VARIANTS_REQUEST,
      payload: {
        ...trackData,
        filterExpression,
        mutantGenomeIds,
      },
    });
    return webApiClient
      .fetchTrackVcfVariants(contig.id, queryRange, refGenomeId, filterExpression, limit, mutantGenomeIds)
      .then((trackVariants: TrackVariants) => {
        dispatch({
          type: LOAD_TRACK_VCF_VARIANTS_SUCCESS,
          payload: {
            ...trackData,
            ...trackVariants,
          },
        });
        return trackVariants;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_VCF_VARIANTS_FAILURE,
          actionPayload: trackData,
          alertTitle: `Fetching Data of VCF Variants Track Failed`,
        }),
      );
  };
}

export function fetchApplicableOrgPropertyTypes(orgIds: number[]) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_APPLICABLE_ORG_PROPERTY_TYPES_REQUEST,
    });
    return webApiClient
      .fetchApplicableOrgPropertyTypes(orgIds)
      .then((orgPropertyTypes: GenomicFeature[]) => {
        dispatch({
          type: LOAD_APPLICABLE_ORG_PROPERTY_TYPES_SUCCESS,
          payload: orgPropertyTypes,
        });
        return orgPropertyTypes;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_APPLICABLE_ORG_PROPERTY_TYPES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function fetchPhenotypeValues() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<OrganismPhenotype[]> => {
    const genomeViewerState = getState().genomeBrowser;
    const mutantGenomeIds: number[] = genomeViewerState.selectedMutantGenomes.map((genome) => genome.id);
    const variantTrack = genomeViewerState.tracks.find(
      (track) => !track.hidden && (track.type === TRACK_TYPE.VARIANT || track.type === TRACK_TYPE.VCF_VARIANT),
    );

    if (!mutantGenomeIds?.length || !variantTrack?.trackCustomization?.configuredPhenotypes?.length) {
      return Promise.resolve([]);
    }

    const phenotypeIds: number[] = variantTrack?.trackCustomization?.configuredPhenotypes
      ?.filter((configuredPhenotype) => configuredPhenotype.idType === PHENOTYPE_TYPE.ORG_PROPERTY)
      .map((configuredPhenotype) => configuredPhenotype.id);
    const attributeIds: number[] = variantTrack?.trackCustomization?.configuredPhenotypes
      ?.filter((configuredPhenotype) => configuredPhenotype.idType === PHENOTYPE_TYPE.ATTRIBUTE)
      .map((configuredPhenotype) => configuredPhenotype.id);

    dispatch({
      type: LOAD_TRACK_PHENOTYPE_VALUES_REQUEST,
    });

    return webApiClient
      .fetchPhenotypeValues(mutantGenomeIds, phenotypeIds, attributeIds)
      .then((values: OrganismPhenotype[]) => {
        dispatch({
          type: LOAD_TRACK_PHENOTYPE_VALUES_SUCCESS,
          payload: values,
        });
        return values;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_TRACK_PHENOTYPE_VALUES_FAILURE,
          //actionPayload: trackData,
          alertTitle: `Fetching of Phenotype Values Failed`,
        }),
      );
  };
}

/**
 * Helpers
 */

export function getAllTracks(
  preConfiguredTracks: ISortableTrack[],
  statistics: GenomeBrowserStatistics,
  additionalVisibleTracks: Array<Partial<Track>> = [],
  userDefinedTracks: Track[] = [],
  jobContext = false,
): ISortableTrack[] {
  const allTracks: ISortableTrack[] = [];

  const isTrackHidden = (track: ISortableTrack): boolean => {
    let hidden = track.hidden ?? true;

    const visibleTracks = additionalVisibleTracks.concat(userDefinedTracks);

    const trackShouldBeVisible =
      visibleTracks?.find((visibleTrack: Track) => isSameTrack(visibleTrack, track)) &&
      !noTrackDataAvailable(track, statistics);

    if (trackShouldBeVisible) {
      hidden = false;
    }

    return hidden;
  };

  // Verify if the current pre-configured tracks list has tracks that doesn't make sense for the current genome
  // As these tracks are already set to visible (hidden=false) we don't check again the ?add_tracks url param
  preConfiguredTracks.forEach((visibleTrack) => {
    let color: string = null;
    let name: string = null;
    switch (visibleTrack.type) {
      // make sure the NUFEATURE track exists for this genome
      case TRACK_TYPE.NUFEATURE:
        const nuFeatureType = statistics?.nuFeaturesCount?.find(
          (nuFeatureType) => nuFeatureType?.featureTypeId === visibleTrack.subTypeId,
        );
        if (!nuFeatureType) {
          return;
        }
        color = nuFeatureType.featureTypeColor; // we need to annotate the track with the color in case the track was restored from a saved view
        name = nuFeatureType.featureTypeName;
        break;
      case TRACK_TYPE.AAFEATURE:
        const aaFeatureType = statistics?.aaFeaturesCount?.find(
          (aaFeatureType) => aaFeatureType?.featureTypeId === visibleTrack.subTypeId,
        );
        if (!aaFeatureType) {
          return;
        }
        color = aaFeatureType.featureTypeColor; // we need to annotate the track with the color in case the track was restored from a saved view
        name = aaFeatureType.featureTypeName;

        break;
      // make sure the MARKER track exists for this genome
      case TRACK_TYPE.MARKER_NUS:
        const markerType = statistics?.markersCount?.find(
          (markerType) => markerType?.markerTypeId === visibleTrack.subTypeId,
        );
        if (!markerType) {
          return;
        }
        name = markerType.markerTypeName;
        break;
      // make sure the PROFILE track exists for this genome
      case TRACK_TYPE.PROFILE:
        const nuProfileType = statistics?.nuProfilesCount?.find(
          (profileFeatureType) => profileFeatureType?.profileTypeId === visibleTrack.subTypeId,
        );
        if (!nuProfileType) {
          return;
        }
        name = nuProfileType.profileTypeName;
        break;
      default:
        const defaultTrack = genomeBrowserDefaultTracks.find((defaultTrack) => defaultTrack.type === visibleTrack.type);
        name = defaultTrack.name;
        break;
    }

    allTracks.push({ ...visibleTrack, color: color, name: name, hidden: false });
  });

  // Add standard tracks if not already added
  genomeBrowserDefaultTracks.forEach((track: ISortableTrack) => {
    if (!allTracks.find((visibleTrack: ISortableTrack) => visibleTrack.type === track.type)) {
      if (!(track.type === TRACK_TYPE.BLAST_ALIGNMENT && !jobContext)) {
        // [SEL9-1300] if the BLAST track is part of the user defined tracks, it should be skipped when there is no job context
        // if not already added to the tracks list we add it
        allTracks.push({ ...track, hidden: isTrackHidden(track) });
      }
    }
  });

  // Add dynamic profile tracks if not already added
  statistics?.nuProfilesCount?.forEach((profileFeatureType) => {
    if (
      !allTracks.find(
        (visibleTrack) =>
          visibleTrack.type === TRACK_TYPE.PROFILE && visibleTrack.name === profileFeatureType.profileTypeName,
      )
    ) {
      const track: ISortableTrack = {
        name: profileFeatureType.profileTypeName,
        type: TRACK_TYPE.PROFILE,
        subTypeId: profileFeatureType.profileTypeId,
        trackCustomization: null,
        pinned: null,
      };
      allTracks.push({
        ...track,
        hidden: isTrackHidden(track),
      });
    }
  });

  // Add dynamic nucleotide feature tracks if not already added
  statistics?.nuFeaturesCount?.forEach((nuFeatureType) => {
    if (
      !allTracks.find(
        (visibleTrack) =>
          visibleTrack.type === TRACK_TYPE.NUFEATURE && visibleTrack.name === nuFeatureType.featureTypeName,
      )
    ) {
      const track: ISortableTrack = {
        name: nuFeatureType.featureTypeName,
        type: TRACK_TYPE.NUFEATURE,
        subTypeId: nuFeatureType.featureTypeId,
        trackCustomization: null,
        color: nuFeatureType.featureTypeColor,
        pinned: null,
      };
      allTracks.push({
        ...track,
        hidden: isTrackHidden(track),
      });
    }
  });

  // Add dynamic amino acid feature tracks if not already added
  statistics?.aaFeaturesCount?.forEach((aaFeatureType) => {
    if (
      !allTracks.find(
        (visibleTrack: ISortableTrack) =>
          visibleTrack.type === TRACK_TYPE.AAFEATURE && visibleTrack.name === aaFeatureType.featureTypeName,
      )
    ) {
      const track: ISortableTrack = {
        name: aaFeatureType.featureTypeName,
        type: TRACK_TYPE.AAFEATURE,
        subTypeId: aaFeatureType.featureTypeId,
        trackCustomization: null,
        color: aaFeatureType.featureTypeColor,
        pinned: null,
      };
      allTracks.push({
        ...track,
        hidden: isTrackHidden(track),
      });
    }
  });

  // Add dynamic marker tracks if not already added
  statistics?.markersCount?.forEach((markerFeatureType) => {
    if (
      !allTracks.find(
        (visibleTrack: ISortableTrack) =>
          visibleTrack.type === TRACK_TYPE.MARKER_NUS && visibleTrack.name === markerFeatureType.markerTypeName,
      )
    ) {
      const track: ISortableTrack = {
        name: markerFeatureType.markerTypeName,
        type: TRACK_TYPE.MARKER_NUS,
        subTypeId: markerFeatureType.markerTypeId,
        trackCustomization: null,
        pinned: null,
      };
      allTracks.push({
        ...track,
        hidden: isTrackHidden(track),
      });
    }
  });

  /**
   Get pinning information from user defined tracks
   */
  allTracks.forEach((visibleTrack: Track) => {
    const userTrack: Track = userDefinedTracks.find((userTrack) => isSameTrack(visibleTrack, userTrack));
    if (userTrack) {
      visibleTrack.pinned = userTrack?.pinned;
    }
  });

  return allTracks;
}

function mergeTrackData(trackData: ITrackData, partialTrackData: Partial<ITrackData>): ITrackData {
  return {
    ...trackData,
    ...partialTrackData,
  };
}

function requestTrackData(
  state: IGenomeBrowserState,
  partialTrackData: Partial<ITrackData>,
  selfManagedLoading = false,
): IGenomeBrowserState {
  const newTrackDataMap: ITrackDataMap = { ...state.trackDataMap };
  const trackId: string = getTrackId(partialTrackData);
  const trackData: ITrackData = state.trackDataMap[trackId];

  newTrackDataMap[trackId] = {
    ...mergeTrackData(trackData, partialTrackData),
    isLoading: !selfManagedLoading, // This is a trick because the NGS tooltip loading need to be self-managed
  };

  return { ...state, trackDataMap: newTrackDataMap };
}

function getInitialTrackData(track: Track) {
  switch (track.type) {
    case TRACK_TYPE.GENE:
    case TRACK_TYPE.PROTEIN_LOCATION:
    case TRACK_TYPE.TRANSCRIPT_LOCATION:
    case TRACK_TYPE.AAFEATURE:
    case TRACK_TYPE.NUFEATURE:
    case TRACK_TYPE.MARKER_NUS:
      return INITIAL_FEATURE_TRACK_DATA;
    case TRACK_TYPE.SEQUENCE:
      return { sequence: null };
    case TRACK_TYPE.VARIANT:
    case TRACK_TYPE.VCF_VARIANT:
      return INITIAL_VARIANT_TRACK_DATA;
    case TRACK_TYPE.PROFILE:
      return INITIAL_PROFILE_TRACK_DATA;
    case TRACK_TYPE.BED:
      return INITIAL_BED_TRACK_DATA;
    case TRACK_TYPE.COORDINATE:
      return INITIAL_COORDINATE_TRACK_DATA;
    case TRACK_TYPE.NGS:
      switch (track.subTypeId) {
        case TRACK_SUBTYPE.NGS_COVERAGE:
          return INITIAL_NGS_COVERAGE_TRACK_DATA;
        case TRACK_SUBTYPE.NGS_PILEUP:
          return INITIAL_NGS_READS_TRACK_DATA;
      }
  }
  return {};
}

function clearAllTrackData(trackDataMap: ITrackDataMap): ITrackDataMap {
  if (!trackDataMap) {
    // can be null because of endpoint errors
    return trackDataMap;
  }

  const newTrackDataMap: ITrackDataMap = { ...trackDataMap };

  Object.keys(newTrackDataMap).forEach((trackId: string) => {
    newTrackDataMap[trackId] = {
      ...trackDataMap[trackId],
      ...getInitialTrackData(trackDataMap[trackId]),
      contig: null,
      range: null,
    };
  });

  return newTrackDataMap;
}

function setTrackData(state: IGenomeBrowserState, partialTrackData: Partial<ITrackData>): IGenomeBrowserState {
  //const trackName: string = partialTrackData.trackName;
  const trackId: string = getTrackId(partialTrackData);
  const contig: Contig = partialTrackData.contig;
  const range: Range = partialTrackData.range;
  const trackData: ITrackData = state.trackDataMap[trackId];

  // Guard against cases when the genome browser has already been unmounted and some data request comes in
  if (!trackData) {
    return state;
  }

  // Update the track data only if the contig and range of the last request match with the contig
  // and range of the trackFeatures. This is because responses might arrive out of order.

  if (!trackData.contig || (trackData.contig.id === contig.id && rangeEquals(trackData.range, range))) {
    // Only one of features, variants and sequence will be actually non-null
    const newTrackData: ITrackData = {
      ...mergeTrackData(trackData, partialTrackData),
      isLoading: false,
    };

    const newTrackDataMap: ITrackDataMap = { ...state.trackDataMap, [trackId]: newTrackData };
    return { ...state, trackDataMap: newTrackDataMap };
  }

  return state;
}

// TODO merge resetTrackData with setTrackData
function resetTrackData(state: IGenomeBrowserState, trackData: Partial<ITrackData>): IGenomeBrowserState {
  const newTrackDataMap: ITrackDataMap = { ...state.trackDataMap };

  if (state.trackDataMap) {
    const trackId: string = getTrackId(trackData);
    // can be null because of endpoint errors
    newTrackDataMap[trackId] = {
      ...state.trackDataMap[trackId],
      isLoading: false,
    };
  }
  return { ...state, trackDataMap: newTrackDataMap };
}

export function initListOfMutantsFromGenome(preselectedIds?: number[], VCFFlavor?: boolean) {
  return (dispatch: ReduxDispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const state: IAppState = getState();
    const genome: Organism = state.genomeBrowser.genome;
    const selectedMutantGenomes = state.genomeBrowser.selectedMutantGenomes;
    const refGenomeId: number = state.auth.session?.params.referenceGenome;

    // in case we have no selected/filtered mutant genomes and the full list of mutant genomes is reasonable then we select them all by default
    if (selectedMutantGenomes.length === 0) {
      const variantAnalysisGroup =
        genome && (genome.id === refGenomeId || VCFFlavor) && genome.variantAnalysisGroups?.[0];

      if (variantAnalysisGroup) {
        if (preselectedIds?.length) {
          // case of preselected ids coming from the url query param, only applies to initialization time
          return dispatch(getMutantGenomesByAnalysisGroup(variantAnalysisGroup, preselectedIds)).then((organisms) => {
            dispatch(setSelectedMutantGenomeList(organisms.map(organismToMutantGenome)));
          });
        } else if (variantAnalysisGroup.totalGenomeCount < GENOME_VIEWER_MUTANT_GENOMES_COUNT_THRESHOLD) {
          // case where the full list of mutant genomes might be automatically added to the track, reasonable amount
          void dispatch(getMutantGenomesByAnalysisGroup(variantAnalysisGroup)).then((genomes) => {
            const mutantGenomes = genomes
              .filter((organism: Organism) => (VCFFlavor ? !organism.isReference : organism.id !== refGenomeId))
              .map(organismToMutantGenome);
            return dispatch(setSelectedMutantGenomeList(mutantGenomes));
          });
        } else {
          dispatch(setSelectedMutantGenomeList([]));
        }
      }
    } else {
      // this case can happen when the ref genome has been switched: we need to exclude the ref genome from the list of mutant genomes in case it was there
      dispatch(
        setSelectedMutantGenomeList(
          selectedMutantGenomes.filter((selectedGenome) => selectedGenome.id !== refGenomeId),
        ),
      );
    }
  };
}

export function searchSequencedGenomes(searchTerm: string, excludeVirtualGenomes = false) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<GenomeSearch> => {
    dispatch({ type: SEARCH_SEQUENCED_GENOMES_REQUEST, payload: searchTerm });

    return webApiClient
      .searchSequencedGenomes(searchTerm, excludeVirtualGenomes)
      .then((response: GenomeSearch) => {
        dispatch({
          type: SEARCH_SEQUENCED_GENOMES_SUCCESS,
          payload: searchTerm,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SEARCH_SEQUENCED_GENOMES_FAILURE,
          alertTitle: "Fetching of Genome Options Failed",
          throwError: true,
        }),
      );
  };
}

export function setGenomeBrowserExpressionColoring(trackType: string, payload: TrackExpressionColoring) {
  return {
    type: SET_TRACK_CUSTOMIZATION,
    payload: {
      trackType: trackType,
      trackCustomization: { expressionColoring: payload } as TrackCustomization,
    },
  };
}

export function setGenomeBrowserPhenotypeConfiguration(trackType: string, payload: TrackPhenotypeConfiguration[]) {
  return {
    type: SET_TRACK_CUSTOMIZATION,
    payload: {
      trackType: trackType,
      trackCustomization: { configuredPhenotypes: payload } satisfies Partial<TrackCustomization>,
    },
  };
}

export function setGenomeBrowserNgsTrackCustomization(payload: Partial<TrackNgsConfiguration>) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    dispatch({
      type: SET_TRACK_CUSTOMIZATION,
      payload: {
        trackType: TRACK_TYPE.NGS,
        trackCustomization: { ngsConfiguration: payload } as TrackCustomization,
      },
    });
    // this customization has an impact on the number of tracks!
    // trigger the update of trackDataMap and the loading of all track data
    const trackList = getState().genomeBrowser.tracks;
    dispatch(setTrackList(trackList));
  };
}

export function setGenomeBrowserBedTrackCustomization(payload: Partial<TrackBedConfiguration>) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    dispatch({
      type: SET_TRACK_CUSTOMIZATION,
      payload: {
        trackType: TRACK_TYPE.BED,
        trackCustomization: { bedConfiguration: payload } as TrackCustomization,
      },
    });
    // this customization has an impact on the number of tracks!
    // trigger the update of trackDataMap and the loading of all track data
    const trackList = getState().genomeBrowser.tracks;
    dispatch(setTrackList(trackList));
  };
}

export function setGenomeBrowserCoordinateTrackCustomization(payload: Partial<TrackCoordinateConfiguration>) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    dispatch({
      type: SET_TRACK_CUSTOMIZATION,
      payload: {
        trackType: TRACK_TYPE.COORDINATE,
        trackCustomization: { coordinateConfiguration: payload } as TrackCustomization,
      },
    });
    // this customization has an impact on the number of tracks!
    // trigger the update of trackDataMap and the loading of all track data
    const trackList = getState().genomeBrowser.tracks;
    dispatch(setTrackList(trackList));
  };
}

export function setNgsTrackBamFileCustomization(bamFileId: number, config: Partial<TrackNgsOrderedBamFilesData>) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const ngsTrack = getState().genomeBrowser.tracks.find((tr) => tr.type === TRACK_TYPE.NGS);
    const ngsConfiguration = ngsTrack?.trackCustomization?.ngsConfiguration;

    const orderedBamFiles: TrackNgsOrderedBamFilesData[] = ngsConfiguration.orderedBamFiles?.map((bamFileData) =>
      bamFileData?.bamFileId === bamFileId ? { ...bamFileData, ...config } : bamFileData,
    );

    dispatch(setGenomeBrowserNgsTrackCustomization({ ...ngsConfiguration, orderedBamFiles }));
  };
}

export function loadDatasetForTrackExpressionColoring(datasetId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Dataset> => {
    /*
    // [SEL6-5100] we do not rely on the cached metadata so that any dataset deletion can cascade through the client as quick as possible
    const existingDataset: IExpressionColoringDatasetLite = getState().genomeBrowser.expressionColoringDatasets[
      datasetId
    ];
    if (existingDataset && existingDataset.dataset) {
      return Promise.resolve(existingDataset.dataset);
    }
    */
    dispatch({
      type: LOAD_DATASET_FOR_TRACK_COLORING_REQUEST,
      payload: datasetId,
    });

    return webApiClient
      .fetchLightDataset(datasetId)
      .then((dataset: Dataset) => {
        if (!dataset) {
          notify.warning(
            {
              title: selMessage({ id: "genomeViewer.expressionColoring.warningTitle" }),
              children: SelMessage({ id: "genomeViewer.expressionColoring.warningMessage" }),
            },
            { autoClose: false },
          );
          // the dataset expression values cannot be shown => clear the track configuration
          dispatch({
            type: LOAD_DATASET_FOR_TRACK_COLORING_FAILURE,
            payload: datasetId,
          });
          return;
        }
        dispatch({
          type: LOAD_DATASET_FOR_TRACK_COLORING_SUCCESS,
          payload: dataset,
        });
        return dataset;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATASET_FOR_TRACK_COLORING_FAILURE,
          alertTitle: "Fetching of Dataset Failed",
          actionPayload: datasetId,
        }),
      );
  };
}

export function loadBamFileSummary(fileId: number) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<BamSummary> => {
    const cachedBamSummary: BamSummary = getState().genomeBrowser.bamDatafiles[fileId];
    if (cachedBamSummary) {
      return Promise.resolve(cachedBamSummary);
    }

    return webApiClient
      .fetchBamFileSummary(fileId)
      .then((bamSummary: BamSummary) => {
        if (bamSummary) {
          dispatch({
            type: LOAD_BAM_FILE_SUMMARY_SUCCESS,
            payload: bamSummary,
          });
        }
        return bamSummary;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_BAM_FILE_SUMMARY_FAILURE,
          actionPayload: { fileId: fileId },
          alertTitle: "BAM File Not Found",
          throwError: true,
          //alertMessage: `The file with id ${fileId} could not be found`,
        }),
      );
  };
}

export function loadBedFile(fileId: number) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<ProjectFile> => {
    const cachedBedFile: ProjectFile = getState().genomeBrowser.bedDatafiles[fileId];
    if (cachedBedFile) {
      return Promise.resolve(cachedBedFile);
    }

    return webApiClient
      .getGenomeViewerFilesById([fileId])
      .then((projectFiles: ProjectFile[]) => {
        const bedFile: ProjectFile = projectFiles?.[0];
        if (bedFile) {
          dispatch({
            type: LOAD_BED_FILE_SUCCESS,
            payload: bedFile,
          });
        }
        return bedFile;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_BED_FILE_FAILURE,
          actionPayload: { fileId: fileId },
          alertTitle: "BED File Not Found",
          throwError: true,
          //alertMessage: `The file with id ${fileId} could not be found`,
        }),
      );
  };
}

export function loadCoordinateList(coordinateListId: number) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<CoordinateList> => {
    const cachedCoordinateList: CoordinateList = getState().genomeBrowser.coordinateLists[coordinateListId];
    if (cachedCoordinateList) {
      return Promise.resolve(cachedCoordinateList);
    }
    return webApiClient
      .fetchCoordinateLists([coordinateListId])
      .then((coordinateLists: CoordinateList[]) => {
        const coordinateList: CoordinateList = coordinateLists?.[0];
        if (coordinateList) {
          dispatch({
            type: LOAD_COORDINATE_LIST_SUCCESS,
            payload: coordinateList,
          });
        }
        return coordinateList;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_COORDINATE_LIST_FAILURE,
          actionPayload: { id: coordinateListId },
          alertTitle: "Coordinate List Not Found",
          throwError: true,
          //alertMessage: `The file with id ${fileId} could not be found`,
        }),
      );
  };
}

export function loadCoordinateLists(ids: number[]) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<CoordinateList[]> => {
    return webApiClient.fetchCoordinateLists(ids).catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_COORDINATE_LISTS_FAILURE,
        actionPayload: { ids: ids },
        alertTitle: "Coordinate List Loading Failed",
        throwError: true,
      }),
    );
  };
}

export function loadCoordinateListsByGenome(genomeId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<CoordinateList[]> => {
    dispatch({
      type: LOAD_COORDINATE_LISTS_BY_GENOME_REQUEST,
    });

    return webApiClient
      .fetchCoordinateListsByGenome(genomeId)
      .then((coordinateLists: CoordinateList[]) => {
        dispatch({
          type: LOAD_COORDINATE_LISTS_BY_GENOME_SUCCESS,
        });
        return coordinateLists;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_COORDINATE_LISTS_BY_GENOME_FAILURE,
          actionPayload: { ids: genomeId },
          alertTitle: "Coordinate List By Genome Loading Failed",
          throwError: true,
        }),
      );
  };
}

export function validateBamFileAgainstRefGenomes(bamFile: ProjectFile) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<string> => {
    const genome = getState().genomeBrowser.genome;

    return webApiClient
      .validateBamFileAgainstRefGenomes(bamFile.id, genome.id !== UNKNOWN_GENOME_ID ? genome.id : null)
      .catch(
        createErrorHandler(dispatch, {
          actionType: BAM_VALIDATION_FAILURE,
          alertTitle: "Could Not Validate BAM File",
        }),
      );
  };
}

export function validateBedFileAgainstRefGenomes(bedFile: ProjectFile) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<string> => {
    const genome = getState().genomeBrowser.genome;

    return webApiClient
      .validateBedFileAgainstRefGenomes(bedFile.id, genome.id !== UNKNOWN_GENOME_ID ? genome.id : null)
      .catch(
        createErrorHandler(dispatch, {
          actionType: BED_VALIDATION_FAILURE,
          alertTitle: "Could not validate BED File",
        }),
      );
  };
}

export function toggleNgsReadsTrack(bamFileId: number) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const ngsTrack = getState().genomeBrowser.tracks.find((track: ISortableTrack) => track.type === TRACK_TYPE.NGS);

    const orderedBamFiles = ngsTrack.trackCustomization.ngsConfiguration.orderedBamFiles.map(
      (bamFile: TrackNgsOrderedBamFilesData) => {
        return bamFile.bamFileId === bamFileId ? { ...bamFile, visiblePileup: !bamFile.visiblePileup } : bamFile;
      },
    );

    dispatch({
      type: SET_TRACK_CUSTOMIZATION,
      payload: {
        trackType: TRACK_TYPE.NGS,
        trackCustomization: {
          ...ngsTrack.trackCustomization,
          ngsConfiguration: {
            ...ngsTrack.trackCustomization.ngsConfiguration,
            orderedBamFiles: orderedBamFiles,
          },
        },
      },
    });
  };
}

export function getMutantGenomesByAnalysisGroup(
  analysisGroup: VariantAnalysisGroup,
  mutantGenomesIds: number[] = undefined,
) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Organism[]> => {
    return webApiClient.getMutantGenomesByAnalysisGroup(analysisGroup, mutantGenomesIds).catch(
      createErrorHandler(dispatch, {
        alertTitle: "Could not fetch list of mutant genomes",
      }),
    );
  };
}

export function getRelatedAnalysisGroups(organismId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<VariantAnalysisGroup[]> => {
    return webApiClient.getRelatedAnalysisGroups(organismId).catch(
      createErrorHandler(dispatch, {
        alertTitle: "Could not fetch list of related analysis groups",
      }),
    );
  };
}

/**
 * This method returns a list of tracks that are
 * - currently displayed and
 * - contain features that can be selected (i.e., there exists a corresponding table with a selection model).
 *
 * @param {Array<ISortableTrack>} tracks
 * @param {boolean} showFlags
 * @return {Array<ISortableTrack>}
 */
export function getDisplayedTracksWithSelectableFeatures(
  tracks: ISortableTrack[],
  showFlags: boolean,
): ISortableTrack[] {
  const displayedTracks: ISortableTrack[] = tracks
    .filter((track: ISortableTrack) => track.type !== TRACK_TYPE.SEQUENCE)
    .filter((track: ISortableTrack) => track.type !== TRACK_TYPE.BLAST_ALIGNMENT)
    .filter((track: ISortableTrack) => track.type !== TRACK_TYPE.PROFILE)
    .filter((track: ISortableTrack) => track.type !== TRACK_TYPE.NGS)
    .filter((track: ISortableTrack) => !track.hidden);
  if (showFlags) {
    // add an ad-hoc flag track (it is not included in the tracks, but controlled by a showFlag boolean)
    displayedTracks.unshift(ARTIFICIAL_FLAGS_TRACK);
  }
  return displayedTracks;
}

export function getFeatureTableRestrictions() {
  return (_dispatch, getState: () => IAppState): InputRestriction => {
    const genomeBrowserState = getState().genomeBrowser;
    const dataTableState = getState().tableData;

    // refGenomeId will be added to the restrictions in a child component
    let restrictions: InputRestriction =
      /*refGenomeState.refGenomeId ? { refGenomeId: refGenomeState.refGenomeId } :*/ {
        variantGenomeIds: genomeBrowserState.selectedMutantGenomes.map((genome: IMutantGenome) => genome.id),
      };
    switch (dataTableState.rowRestriction) {
      case TableRestriction.GENOME:
        break;
      case TableRestriction.REGION:
        if (genomeBrowserState.contig && genomeBrowserState.range) {
          const rangeRegion: InputRegionWithoutStrand = {
            contigId: genomeBrowserState.contig.id,
            range: genomeBrowserState.range,
          };
          restrictions = { ...restrictions, region: rangeRegion };
        }
        break;
      case TableRestriction.CONTIG:
        if (genomeBrowserState.contig) {
          const contigRegion: InputRegionWithoutStrand = { contigId: genomeBrowserState.contig.id };
          restrictions = { ...restrictions, region: contigRegion };
        }
        break;
      case TableRestriction.SELECTION:
        // we do not have the active tab context here (do want to get rid of activeTab redux property because it leads to misuse and error prone processes)
        // therefore we only set the selection to empty, and the real selection will be picked up by the DataTableRestrictions component (for all of the living tabs !)
        restrictions = { ...restrictions, selection: { selectedResultIds: [] } };
        break;
    }
    return restrictions;
  };
}

export function getAllTableSpecsFromTrackUsingGenomeInformation(track: ISortableTrack, filterByAvailability = true) {
  return (_dispatch, getState: () => IAppState): InputTableQuerySpec[] => {
    const state: IAppState = getState();
    const genomeBrowserState = state.genomeBrowser;
    const refGenomeId: number = state.auth.session?.params.referenceGenome;

    return genomeBrowserState.genome
      ? getAllTableSpecsFromSortableTrack(
          track,
          genomeBrowserState.genome?.id,
          refGenomeId,
          genomeBrowserState.bedDatafiles,
          genomeBrowserState.coordinateLists,
          filterByAvailability,
        )
      : [];
  };
}

export function getTableSpecFromTrackUsingGenomeInformation(
  track: ISortableTrack,
  fileId?: number,
  coordinateListId?: number,
) {
  return (_dispatch, getState: () => IAppState): InputTableQuerySpec => {
    const state: IAppState = getState();
    const genomeBrowserState = state.genomeBrowser;
    const refGenomeId: number = state.auth.session && state.auth.session.params.referenceGenome;
    return getTableSpecFromSortableTrack(track, {
      fileId,
      genomeId: genomeBrowserState.genome?.id,
      refGenomeId,
      coordinateListId,
    });
  };
}

export function getAllTableSpecsFromSortableTrack(
  track: ISortableTrack,
  genomeId: number = null,
  refGenomeId: number = null,
  bedFiles: Record<number, ProjectFile> = {},
  coordLists: Record<number, CoordinateList> = {},
  filterByAvailability = true,
): InputTableQuerySpec[] {
  if (track.type === TRACK_TYPE.VCF_VARIANT) {
    return [getTableSpecFromSortableTrack(track, { genomeId })];
  } else if (track.type === TRACK_TYPE.VARIANT) {
    return [getTableSpecFromSortableTrack(track, { genomeId, refGenomeId })];
  } else if (track.type === TRACK_TYPE.BED) {
    return (
      track.trackCustomization?.bedConfiguration?.orderedBedFiles
        ?.filter((bedFile) => {
          if (filterByAvailability) {
            const projectFile = bedFiles[bedFile.bedFileId];
            return projectFile && !projectFile.hidden && !projectFile.deleted;
          }
          return true;
        })
        .map((bedFile) => getTableSpecFromSortableTrack(track, { fileId: bedFile.bedFileId, genomeId })) ?? []
    );
  } else if (track.type === TRACK_TYPE.COORDINATE) {
    return (
      track.trackCustomization?.coordinateConfiguration?.orderedCoordinateLists
        ?.filter((coordList) => {
          if (filterByAvailability) {
            const list = coordLists[coordList.coordinateListId];
            return list && !list.hidden && !list.deleted;
          }
          return true;
        })
        ?.map((coordinateList) =>
          getTableSpecFromSortableTrack(track, { coordinateListId: coordinateList.coordinateListId, genomeId }),
        ) ?? []
    );
  } else {
    return [getTableSpecFromSortableTrack(track, { genomeId })];
  }
}

export function getTableSpecFromSortableTrack(
  track: Partial<Track>,
  info?: { genomeId?: number; refGenomeId?: number; fileId?: number; coordinateListId?: number },
): InputTableQuerySpec {
  if (track.type === TRACK_TYPE.BED) {
    return createTableSpecForFileParent(info?.fileId, ENTITY_TYPE.BED_FEATURE, info?.genomeId);
  } else if (track.type === TRACK_TYPE.COORDINATE) {
    return createTableSpecForCoordinateListParent(info?.coordinateListId, ENTITY_TYPE.COORDINATE, info?.genomeId);
  } else {
    return createTableSpecForOrganismParent(
      track.type,
      info?.genomeId,
      track.type === TRACK_TYPE.VARIANT ? info?.refGenomeId : null,
      track.subTypeId,
    );
  }
}

export function getClassicGenomeBrowserTracks(trackTypes: string[]): ISortableTrack[] {
  return trackTypes.map(
    (trackType: string) =>
      genomeBrowserDefaultTracks.find((track: Partial<ISortableTrack>) => track.type === trackType) as ISortableTrack,
  );
}

export function createMutantGenomesTableQuerySpec(
  variantTableSpec: InputTableQuerySpec,
  genome: Organism,
  selectedMutantGenomes?: IMutantGenome[],
): InputTableQuerySpec {
  if (variantTableSpec) {
    // here is only one single variant analysis group
    const analysisGroup = genome.variantAnalysisGroups?.[0];
    // if there is no mutant genome was selected, we do not show the table
    if (selectedMutantGenomes?.length && analysisGroup) {
      return {
        ...createTableSpecForAnalysisGroupParent(analysisGroup),
        restrictions: {
          variantGenomeIds: selectedMutantGenomes.map((mutantGenome) => mutantGenome.id),
        },
      } satisfies InputTableQuerySpec;
    }
  }
  return null;
}

export function getShowRelatedVariantRoute(genome: IMutantGenome, variant: Variant) {
  return `/variant/${variant.mutId}/genome/${genome.id}`;
}

export function loadVariantRelatedGenomes(tableSpec: ITableSpecWithContext) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<number[]> => {
    return webApiClient.getGenomeIDsForVariants(tableSpec).catch(
      createErrorHandler(dispatch, {
        actionType: LOG_APPLICATION_ERROR,
        throwError: true,
        showAlert: false,
      }),
    );
  };
}

export function createVariantFilterPayload(
  variantFilters: IVariantFilterableProperty[],
  tableColumn: ITableColumn,
  tableSpec: InputTableQuerySpec,
  field: string,
): IFilterPayload {
  const filteredKeys: string[] = variantFilters
    .filter((variantFilter: IVariantFilterableProperty) => variantFilter.filtered)
    .map((variantFilter: IVariantFilterableProperty) => variantFilter.key);

  const rsqlFilter: IRsqlComparisonExpr = filteredKeys.length !== tableColumn.searchAttributes.values.length && {
    key: field,
    operator: SEARCH_OPERATORS.IN,
    value: filteredKeys.length > 0 ? filteredKeys : [""], // workaround for SEL5-1282
  };
  return {
    columnField: field,
    tableSpec: tableSpec,
    filterExpression: rsqlFilter ? encodeFilter(rsqlFilter) : "", // in case all options are selected, allows to overwrite the existing filter if any
  };
}

export function createProfileFilterPayload(
  profiles: IMutantGenome[],
  tableSpec: InputTableQuerySpec,
  field: string,
): IFilterPayload {
  const filteredKeys: string[] = profiles
    //.filter((variantFilter: IMutantGenome) => variantFilter.filtered)
    .map((variantFilter: IMutantGenome) => variantFilter.name);

  const rsqlFilter: IRsqlComparisonExpr = /*filteredKeys.length !== profiles.length &&*/ {
    key: field,
    operator: SEARCH_OPERATORS.IN,
    value: filteredKeys.length > 0 ? filteredKeys : [""], // workaround for SEL5-1323
  };
  return {
    columnField: field,
    tableSpec: tableSpec,
    filterExpression: rsqlFilter ? encodeFilter(rsqlFilter) : "", // in case all options are selected, allows to overwrite the existing filter if any
  };
}

export function createExpressionColoringConditionNameLookup(dataset: Dataset): IConditionNameLookup {
  // make sure that the lookup is populated with every condition that may come up
  const conditionNameLookup: IConditionNameLookup = {};
  if (dataset) {
    dataset.columns.forEach((dc: DatasetColumn) => {
      conditionNameLookup[dc.field] = dc.name;
    });
  }
  return conditionNameLookup;
}

function calculateNgsCoverageRangeLimits(coverageRaster: number[]): INgsCoverageRangeLimits {
  // No need to calculate the min value for linear scaling because it is always 0
  // For the log scaling, the min is the smallest number above 0
  const valuesWithoutZeros: number[] = coverageRaster.filter((n: number) => n > 0);

  // if there are only zero values, return both limits as null
  return valuesWithoutZeros.length === 0
    ? { minLog: null, max: null }
    : { minLog: Math.min(...valuesWithoutZeros), max: Math.max(...valuesWithoutZeros) };
}

export function getTrackId(track: Partial<ITrackData>) {
  return `${track.type}${track.subTypeId ? "-" + track.subTypeId : ""}${track.fileId ? "-" + track.fileId : ""}${
    track.coordinateListId ? "-" + track.coordinateListId : ""
  }${track.trackSubtype ? "-" + track.trackSubtype : ""}`;
}

export function getTrackIdFromTableSpec(track: InputTableQuerySpec) {
  const trackIdentification: ITrackIdentification = {
    // TRACK_TYPE must correspond to ENTITY_TYPE
    type: track.resultType,
    subTypeId: track.queryType === DATATABLE_PARENT_TYPE.FILE ? null : track.resultSubTypeId,
    fileId: track.queryType === DATATABLE_PARENT_TYPE.FILE ? track.id : null,
  };
  return getTrackId(trackIdentification);
}

export function isSamePhenotypeType(
  p1: GenomicFeature | TrackPhenotypeConfiguration,
  p2: GenomicFeature | TrackPhenotypeConfiguration,
) {
  return p1.id === p2.id && p1.idType === p2.idType;
}
